/* ============================================================================
 * ornament-editor.js — personalized sphere-ornament design engine (KOBLO)
 *
 * Sibling to lithophane-editor.js (the nightlight): same backend protocol
 * (/reserve → /save → add-to-cart with _job_id), same lw- design system and
 * shared editor-theme tokens, full text parity (2 layers, fonts, B/I/U,
 * outline, curved, align, color, drag/resize on canvas) — but a SPHERE-native
 * canvas:
 *   • the design canvas IS the equirectangular texture wrapped onto the ball
 *   • optional sphere-correct (inverse-spherical) pre-warp around a pivot
 *   • text is baked flat onto the texture (like the nightlight); the STL/3MF
 *     is generated from the finished texture on the backend
 *   • preview = Three.js ball + threaded cap (instant rotation, no re-bake)
 *
 * Product config: window.ORNAMENT_CONFIG · shared: window.KOBLO_EDITOR_DEFAULTS
 * ==========================================================================*/
(function () {
  "use strict";
  var DEF = window.KOBLO_EDITOR_DEFAULTS || {};
  var STR = DEF.strings || {};
  function T(k, fb) { return (STR[k] != null) ? STR[k] : fb; }   // shared verbiage, with fallback
  var CFG = window.ORNAMENT_CONFIG || {};

  // Warm the tree backdrop into the browser cache immediately. initThree() is
  // only called on the first setMode("preview"), and its TextureLoader starts
  // the 369 KB fetch AT THAT MOMENT — so the ball rendered instantly while the
  // tree popped in a second or two later. Kicking the same URL off here means
  // it is already cached by the time anyone finishes placing a photo, and the
  // TextureLoader resolves from cache. Costs one request the page was going to
  // make anyway; if it somehow hasn't finished, behaviour is exactly as before.
  if (CFG.backdropUrl) { var _bgWarm = new Image(); _bgWarm.decoding = "async"; _bgWarm.src = CFG.backdropUrl; }
  var API = CFG.apiBase || DEF.apiBase;
  var FONTS = DEF.fonts || [
    { name: "Playfair Display", css: "'Playfair Display', serif" },
    { name: "Montserrat", css: "'Montserrat', sans-serif" },
    { name: "Dancing Script", css: "'Dancing Script', cursive" },
    { name: "Oswald", css: "'Oswald', sans-serif" },
    { name: "Anton", css: "'Anton', sans-serif" },
    { name: "Archivo Black", css: "'Archivo Black', sans-serif" }
  ];
  var TEXTDEF = DEF.text || { maxLength: 40, maxLayers: 2, minSizePt: 10, defaultFont: "Playfair Display" };
  var PHOTODEF = DEF.photo || { zoomMin: 0.2, zoomMax: 4, lowResMinPx: 700 };
  var SIZES = CFG.sizes || [];
  var $ = function (id) { return document.getElementById(id); };
  var TAU = Math.PI * 2;
  function isMobile() { return !!(window.matchMedia && window.matchMedia("(max-width: 820px)").matches); }

  function fontCss(name) {
    for (var i = 0; i < FONTS.length; i++) if (FONTS[i].name === name) return FONTS[i].css;
    return "'" + name + "', sans-serif";
  }

  // ── working state ──────────────────────────────────────────────────────
  function newText(y) {
    return { txt: "", font: TEXTDEF.defaultFont, size: 22, color: "dark",
      bold: false, italic: false, underline: false, outline: false,
      curved: false, curveAmount: 50, curveFlip: false, align: "center", x: 0.5, y: y,
      boxW: 0 };   // 0 = auto (box hugs the text); >0 = widened box (fraction of W) for align room
  }
  var S = {
    size: SIZES[0] || { key: "standard3", diameter: 83.9, hole: 23.1, minT: 0.84, maxT: 2.52 },
    img: null, natW: 0, natH: 0, photoData: null, cleanBlob: null, cleanName: null, editCleanExt: null,
    zoom: 1, offX: 0, offY: 0,
    sc: false, scStrength: 1.0, mu: 0.5, mv: 0.5,
    bgFront: "gray",                // FRONT background: 'gray'(default) | 'extend'(blur) | 'white' | 'dark'
    bgBack: "gray",                 // BACK background — chosen independently so the front blur never bleeds to the back
    side: "front",                  // which face is being edited: 'front' (photo+text) | 'back' (text only)
    texts: [null, null],            // ACTIVE side's text layers (swapped with backTexts on side change)
    frontTexts: [null, null],       // stored front text while editing the back
    backTexts: [null, null],        // stored back text while editing the front (text only — no photo on the back)
    sel: -1,                        // selected layer index, -1 none
    mode: "design", tool: "photo",
    jobId: null, variantId: null, reserving: null, lowResAccepted: false,
    editJobId: null, editCartLine: null, editFrom: null,
    hydrating: false,               // true while an edit is loading (guards upload-zone flash + dirty flag)
    editDirty: false,               // in edit mode, "Save Design" stays disabled until a real change
    previewReady: false,            // Add-to-Cart unlocks only after Preview renders (nightlight parity)
    rotation: 0, origImg: null      // 0/90/180/270 — baked into S.img, never sent to backend
  };
  var DEFAULT_TEXT = "Your Text Here";   // placeholder for a freshly-added text layer
  var MAX_CHARS = TEXTDEF.maxLength || 40;
  // Stay silent until the limit, then show a brief red notice (nightlight parity).
  function setCharNotice(el, len) {
    if (!el) return;
    if (len >= MAX_CHARS) { el.textContent = "Max " + MAX_CHARS + " chars"; el.classList.add("lw-char-max"); }
    else { el.textContent = ""; el.classList.remove("lw-char-max"); }
  }

  // ── geometry / texture dims ────────────────────────────────────────────
  function geom(sz) {
    var R = sz.diameter / 2, holeR = sz.hole / 2;
    var thetaTop = Math.asin(Math.min(1, holeR / R));
    return { R: R, holeR: holeR, thetaTop: thetaTop, thetaBot: Math.PI,
             aspect: TAU / (Math.PI - thetaTop) };
  }
  function vOf(theta) { var g = geom(S.size); return (theta - g.thetaTop) / (g.thetaBot - g.thetaTop); }
  function vEquator() { return vOf(Math.PI / 2); }
  // Photo-snap window in SCREEN pixels, like Figma/Illustrator — constant felt
  // distance whatever the stage size. MUST match SNAP_PX in lithophane-editor.js.
  var SNAP_PX = 2;
  // EXPORT_W raised 1756 -> 2800 so the backend has a picture comfortably larger
  // than the mesh (1728 cols) and always has something to average, killing the
  // 8-bit thickness terracing at the source instead of only working around it in
  // the mesh sampling. The backend caps its working width at 2200, so 2800->2200
  // there and 2200->1728 in the mesh sampler are two gentle ~1.27x downsamples
  // that land at 0% terracing on both axes; it also unlocked the 6in going to
  // 0.25mm rows (was 0.265, forced by the smaller texture). Only the EXPORT bake
  // pays the extra pixels, once at checkout, not the live editing bakes (STAGE_W /
  // PREVIEW3D_W are unchanged), so dragging stays smooth.
  var PREVIEW_W = 1024, PREVIEW3D_W = 2200, EXPORT_W = 2800;   // 2D-design / 3D-preview map / print texture
  var STAGE_W = 2048;   // 2D stage bake width — high enough that the centre-crop stays crisp on 2× displays
  var WRAP_ASPECT = 2.1948;   // FIXED display aspect for the editor canvas — keeps the canvas the SAME size
                              // for 3″ and 6″ (their true sphere aspects differ ~0.7%, invisible; the PRINT
                              // texture still bakes at each size's real geom aspect)
  // Tree-photo backdrop framing: CSS background-size / -position on the preview
  // wrap, tuned so the tree's white hook-outline sits centred behind the ball.
  // The hook-outline sits at the image centre, so "center" makes the tree scale
  // around the same point as the ball → they zoom together without drifting.
  var ORN_BACKDROP = { size: "auto 100%", pos: "50% 50%" };
  function texDims(w) { var g = geom(S.size); return { w: w, h: Math.max(1, Math.round(w / g.aspect)) }; }

  var texCanvas = document.createElement("canvas");
  var tctx = texCanvas.getContext("2d");

  // ── photo placement (offsets stored at PREVIEW_W scale) ────────────────
  // Cover-fit the photo to the FRONT hemisphere (centre half, w/2 wide) — the
  // photo lives in the centre of the wrap, not all the way around.
  function baseCover(w, h) { return S.img ? Math.max((w / 2) / S.natW, h / S.natH) : 1; }
  function placement(w, h) {
    var k = w / PREVIEW_W;
    var pw = S.natW * baseCover(w, h) * S.zoom, ph = S.natH * baseCover(w, h) * S.zoom;
    return { w: pw, h: ph, x: (w - pw) / 2 + S.offX * k, y: (h - ph) / 2 + S.offY * k };
  }
  // Does the placed photo leave any of the front band uncovered? (Cover-fit fills
  // it at zoom 1; gaps appear when zoomed out or panned off.) Drives whether the
  // Background-fill option is offered.
  function photoHasGaps() {
    if (!S.img) return false;
    var dim = texDims(PREVIEW_W), W = dim.w, H = dim.h, p = placement(W, H);
    var bx0 = FRONT_U0 * W, bx1 = FRONT_U1 * W, eps = 2;
    return p.x > bx0 + eps || (p.x + p.w) < bx1 - eps || p.y > eps || (p.y + p.h) < H - eps;
  }
  function samplePhoto(px, py) {
    if (!S.photoData || px < 0 || py < 0 || px >= S.natW || py >= S.natH) return 255;
    var i = (((py | 0) * S.natW) + (px | 0)) * 4, d = S.photoData;
    return 0.299 * d[i] + 0.587 * d[i + 1] + 0.114 * d[i + 2];
  }

  // ── bake: photo (plain or sphere-corrected) + text → equirect texture ──
  // Background fills. 'gray' is a softly speckled mid-gray — prints as a
  // frosted medium-thickness surround instead of white's bright glow. The
  // noise pattern is generated once and reused so re-bakes don't shimmer.
  var _grayPattern = null;
  function bgFill(ctx2, mode) {
    if (mode === "white") return "#ffffff";
    if (mode === "dark") return "#3c3c3c";
    if (!_grayPattern) {
      var pc = document.createElement("canvas"); pc.width = 256; pc.height = 256;
      var px = pc.getContext("2d");
      px.fillStyle = "#8f8f8f"; px.fillRect(0, 0, 256, 256);
      for (var i = 0; i < 2600; i++) {
        var v = 123 + Math.floor(Math.random() * 40);      // ±20 around the base
        px.fillStyle = "rgba(" + v + "," + v + "," + v + ",0.5)";
        px.fillRect(Math.random() * 256, Math.random() * 256, 1.6, 1.6);
      }
      _grayPattern = ctx2.createPattern(pc, "repeat");
    }
    return _grayPattern;
  }
  function bgSolid(mode) { return mode === "white" ? "#ffffff" : mode === "dark" ? "#3c3c3c" : "#8f8f8f"; }
  // Which background applies to a given face ('front'|'back'); each side is chosen
  // independently so the front's blur never bleeds onto the back.
  function bgOf(side) { return side === "back" ? S.bgBack : S.bgFront; }

  // Auto edge-blur extend: fill the whole canvas with a heavily blurred,
  // slightly dimmed cover-fit of the photo (drawn wrapped 3× so the 360° seam
  // stays soft), then the sharp photo draws on top. Cached — the fill depends
  // only on the photo and bake width, not placement, so drags stay fast.
  // Stable identity for the current photo. After a rotate, S.img is a CANVAS
  // (no .src) — keying off S.img.src then throws / cache-collides. S.origImg is
  // always the real uploaded Image, and rotation is folded in separately.
  function imgKey() {
    var s = (S.origImg && S.origImg.src) ? S.origImg.src : ((S.img && S.img.src) ? S.img.src : "");
    return s.slice(-40) + "|r" + (S.rotation || 0);
  }
  var _extendCache = { key: null, canvas: null };
  function extendFill(W, H) {
    var key = imgKey() + "|" + W;
    if (_extendCache.key === key) return _extendCache.canvas;
    var c = document.createElement("canvas"); c.width = W; c.height = H;
    var x = c.getContext("2d");
    x.fillStyle = "#8f8f8f"; x.fillRect(0, 0, W, H);
    if (S.img) {
      var cov = Math.max(W / S.natW, H / S.natH) * 1.08;
      var cw = S.natW * cov, ch = S.natH * cov;
      // Blur via downscale→upscale rather than ctx.filter: ctx.filter="blur()" is
      // a silent no-op on older mobile Safari (< iOS 17), which left the fill
      // sharp on phones. Drawing the wrapped cover tiny then scaling it back up
      // with smoothing gives an equivalent soft blur on every browser (and is
      // cheaper).
      var tmp = document.createElement("canvas"); tmp.width = W; tmp.height = H;
      var tx = tmp.getContext("2d");
      [-W, 0, W].forEach(function (dx) {
        tx.drawImage(S.img, (W - cw) / 2 + dx, (H - ch) / 2, cw, ch);
      });
      var dw = Math.max(3, Math.round(W / 16)), dh = Math.max(3, Math.round(H / 16));   // moderate blur (was too heavy)
      var sm = document.createElement("canvas"); sm.width = dw; sm.height = dh;
      var sx = sm.getContext("2d"); sx.imageSmoothingEnabled = true; sx.imageSmoothingQuality = "high";
      sx.drawImage(tmp, 0, 0, W, H, 0, 0, dw, dh);        // shrink (averages neighbourhoods)
      x.imageSmoothingEnabled = true; x.imageSmoothingQuality = "high";
      x.drawImage(sm, 0, 0, dw, dh, 0, 0, W, H);           // grow back → soft blur
      x.fillStyle = "rgba(0,0,0,0.10)"; x.fillRect(0, 0, W, H);   // slight dim so the sharp photo pops
    }
    _extendCache.key = key; _extendCache.canvas = c;
    return c;
  }

  // Pole fade: the texture's top/bottom rows each collapse onto a single point
  // of the sphere, so content there smears into a pinwheel. Blend the last ~2%
  // of latitude into the background for a clean finish.
  function poleFade(W, H, solid) {
    var fade = Math.max(2, Math.round(H * 0.02));
    var gb = tctx.createLinearGradient(0, H - fade, 0, H);
    gb.addColorStop(0, solid + "00"); gb.addColorStop(1, solid);
    tctx.fillStyle = gb; tctx.fillRect(0, H - fade, W, fade);
    var gt = tctx.createLinearGradient(0, fade, 0, 0);
    gt.addColorStop(0, solid + "00"); gt.addColorStop(1, solid);
    tctx.fillStyle = gt; tctx.fillRect(0, 0, W, fade);
  }

  // Clean surround for the sides + back — a plain speckled grey/colour fill, never
  // the blurred photo, so the sides stay clean (no image wrapping around).
  function fillSurround(W, H, mode) {
    // 'extend' = a blurred, wrapped cover of the photo. White/gray/dark = a clean
    // solid fill. Called per-region with the front's or the back's mode.
    if (mode === "extend") {
      if (S.img) { tctx.drawImage(extendFill(W, H), 0, 0); return; }
      tctx.fillStyle = bgFill(tctx, "gray"); tctx.fillRect(0, 0, W, H); return;   // no photo yet → plain grey
    }
    tctx.fillStyle = bgFill(tctx, mode);
    tctx.fillRect(0, 0, W, H);
  }

  // Front centre band on the wrap: u ∈ [0.25, 0.75] (the front hemisphere). The
  // photo is confined here; the back text lives on the two sides (u<0.25 & u>0.75).
  var FRONT_U0 = 0.25, FRONT_U1 = 0.75;

  // Back text is authored on a back-centred canvas (x=0.5 = the back meridian),
  // which on the wrap is the seam (u=0/1). Shift by 180° and draw across the seam
  // (±W) so a straddling word renders whole — the two halves land on either side
  // of the front photo and meet at the back. Clipped to the back sides so it can
  // never spill onto the front image.
  function drawSeamText(ctx, t, W, H) {
    ctx.save();
    ctx.beginPath();
    ctx.rect(0, 0, FRONT_U0 * W, H); ctx.rect(FRONT_U1 * W, 0, (1 - FRONT_U1) * W, H);
    ctx.clip();
    var xc = ((t.x + 0.5) % 1 + 1) % 1;
    [xc - 1, xc, xc + 1].forEach(function (x) {
      var tt = {}; for (var k in t) tt[k] = t[k]; tt.x = x;
      drawText(ctx, tt, W, H);
    });
    ctx.restore();
  }

  // Every text layer for a view, painted onto ANY context in that context's own
  // pixel basis. Layer positions are texture FRACTIONS and font size is a % of
  // height, so W/H may be the texture's dimensions (the print/3D bake) or the
  // on-screen canvas's (the 2D stage) — the text lands identically either way,
  // just rasterized at that resolution.
  function paintText(ctx, view, W, H) {
    if (view === "back") {
      var bt = backTextsNow();
      for (var bi = 0; bi < bt.length; bi++) if (bt[bi] && bt[bi].txt) drawText(ctx, bt[bi], W, H);
      return;
    }
    var ft = frontTextsNow();
    if (ft.some(function (t) { return t && t.txt; })) {
      // Front text lives on the centre face only — clip it to the same band as
      // the photo so anything past the canvas edge is cropped, not wrapped onto
      // the sides/back.
      ctx.save();
      ctx.beginPath();
      ctx.rect(FRONT_U0 * W, 0, (FRONT_U1 - FRONT_U0) * W, H);
      ctx.clip();
      for (var i = 0; i < ft.length; i++) if (ft[i] && ft[i].txt) drawText(ctx, ft[i], W, H);
      ctx.restore();
    }
    if (view === "wrap") {
      var bt2 = backTextsNow();
      for (var k = 0; k < bt2.length; k++) if (bt2[k] && bt2[k].txt) drawSeamText(ctx, bt2[k], W, H);
    }
  }

  // Photo layer = clean surround + the front photo (confined to the centre band)
  // + the sphere-correct warp. It's identical for the 2D 'front' view and the
  // 3D/print 'wrap', so it's CACHED — text edits (the common case) reuse it and
  // never re-run the expensive per-pixel warp. The warp is SKIPPED live while the
  // photo is being dragged (keeps the drag smooth); it snaps back on release.
  var _draggingPhoto = false;
  var _plCache = { key: null, canvas: null };
  function bakePhotoLayer(W, H) {
    var warp = S.sc && S.scStrength > 0 && !_draggingPhoto;
    var key = W + "|" + (S.img ? imgKey() : "0") + "|" + S.zoom + "|" +
      Math.round(S.offX) + "|" + Math.round(S.offY) + "|" + (warp ? "w" : "f") + "|" +
      S.bgFront + "|" + S.bgBack + "|" + S.size.key;
    texCanvas.width = W; texCanvas.height = H;
    if (!_draggingPhoto && _plCache.key === key && _plCache.canvas) {
      tctx.drawImage(_plCache.canvas, 0, 0);
      return;
    }
    // 1) The whole surround (sides + back) = the BACK background.
    fillSurround(W, H, S.bgBack);
    // 2) The front centre band = the FRONT background + photo, clipped so the
    //    front's blur/fill stays on the front and never reaches the back.
    var p = placement(W, H);
    tctx.save();
    tctx.beginPath(); tctx.rect(FRONT_U0 * W, 0, (FRONT_U1 - FRONT_U0) * W, H); tctx.clip();
    fillSurround(W, H, S.bgFront);
    if (S.img) {
      tctx.imageSmoothingQuality = "high";
      tctx.drawImage(S.img, p.x, p.y, p.w, p.h);
    }
    tctx.restore();
    if (S.img && warp) spherize(W, H, p);   // sphere-correct, always centred, bounded to the centre by Rx = W/4
    poleFade(W, H, bgSolid(S.bgBack));
    if (!_draggingPhoto) {
      var c = _plCache.canvas || document.createElement("canvas");
      c.width = W; c.height = H; c.getContext("2d").drawImage(texCanvas, 0, 0);
      _plCache.canvas = c; _plCache.key = key;
    }
  }

  // view: 'front' (photo layer + front text) | 'back' (clean surround + back text,
  //   centred, for editing) | 'wrap' (photo layer + front text + back text split
  //   onto the two sides at the seam) — the full print texture.
  // opts.noText leaves the text off (the 2D stage paints it at screen resolution
  // instead); the print + 3D bakes always include it.
  function bake(w, opts) {
    opts = opts || {};
    var view = opts.view || "wrap";
    var dim = texDims(w), W = dim.w, H = dim.h;

    if (view === "back") {
      texCanvas.width = W; texCanvas.height = H;
      fillSurround(W, H, S.bgBack);
      poleFade(W, H, bgSolid(S.bgBack));
    } else {
      bakePhotoLayer(W, H);   // clean surround + centre photo + sphere-correct (cached)
    }
    if (!opts.noText) paintText(tctx, view, W, H);
    return texCanvas;
  }

  // Sphere pre-warp = Photoshop "Spherize" at −100% (pincushion): pinches the
  // photo radially INWARD to pre-compensate the outward bulge it picks up when
  // wrapped onto the ball. Applied within an ellipse inscribed in the front band
  // (u ∈ [0.25, 0.75]) so — exactly like Photoshop — the warp is radial and the
  // corners stay put. Cached in bakePhotoLayer. Tune SC_AMOUNT for strength
  // (1 = full −100%, lower = gentler).
  var SC_AMOUNT = 1.0;
  function spherize(W, H) {
    var cx = 0.5 * W, cy = 0.5 * H;            // centre of the front band
    var Rx = W / 4, Ry = H / 2;                // ellipse inscribed in the band
    if (Rx < 4 || Ry < 4) return;
    var x0 = Math.max(0, Math.floor(cx - Rx)), y0 = Math.max(0, Math.floor(cy - Ry));
    var x1 = Math.min(W, Math.ceil(cx + Rx)), y1 = Math.min(H, Math.ceil(cy + Ry));
    var bw = x1 - x0, bh = y1 - y0;
    if (bw <= 0 || bh <= 0) return;
    var src = tctx.getImageData(x0, y0, bw, bh);
    var out = tctx.createImageData(bw, bh);
    var sd = src.data, od = out.data, a = SC_AMOUNT, HPI = Math.PI / 2;
    for (var yy = 0; yy < bh; yy++) {
      for (var xx = 0; xx < bw; xx++) {
        var i = (yy * bw + xx) * 4;
        var dx = (x0 + xx) - cx, dy = (y0 + yy) - cy;
        var nx = dx / Rx, ny = dy / Ry;
        var rho = Math.sqrt(nx * nx + ny * ny);            // elliptical radius, 1 = rim
        if (rho >= 1 || rho === 0) {
          od[i] = sd[i]; od[i + 1] = sd[i + 1]; od[i + 2] = sd[i + 2]; od[i + 3] = sd[i + 3];
          continue;
        }
        var f = (1 - a) * rho + a * Math.sin(rho * HPI);   // f ≥ rho → centre pinches inward (−100% spherize)
        var k = f / rho;
        var sx2 = cx + dx * k - x0, sy2 = cy + dy * k - y0;
        // BILINEAR, not nearest. The warp stretches some regions and squeezes
        // others, so snapping each destination pixel to one source pixel threw
        // away sub-pixel detail and left jagged edges — baked permanently into
        // the print texture. Blending the 4 neighbours costs nothing in file
        // size or mesh, and this runs on the EXPORT bake, not just the preview.
        if (sx2 < 0) sx2 = 0; else if (sx2 > bw - 1) sx2 = bw - 1;
        if (sy2 < 0) sy2 = 0; else if (sy2 > bh - 1) sy2 = bh - 1;
        var sxi = sx2 | 0, syi = sy2 | 0;
        var fx = sx2 - sxi, fy = sy2 - syi;
        var sxn = sxi + 1 < bw ? sxi + 1 : sxi, syn = syi + 1 < bh ? syi + 1 : syi;
        var j00 = (syi * bw + sxi) * 4, j10 = (syi * bw + sxn) * 4;
        var j01 = (syn * bw + sxi) * 4, j11 = (syn * bw + sxn) * 4;
        var w00 = (1 - fx) * (1 - fy), w10 = fx * (1 - fy),
            w01 = (1 - fx) * fy,       w11 = fx * fy;
        od[i]     = sd[j00]     * w00 + sd[j10]     * w10 + sd[j01]     * w01 + sd[j11]     * w11;
        od[i + 1] = sd[j00 + 1] * w00 + sd[j10 + 1] * w10 + sd[j01 + 1] * w01 + sd[j11 + 1] * w11;
        od[i + 2] = sd[j00 + 2] * w00 + sd[j10 + 2] * w10 + sd[j01 + 2] * w01 + sd[j11 + 2] * w11;
        od[i + 3] = 255;
      }
    }
    tctx.putImageData(out, x0, y0);
  }

  // font px in texture space: size = % of texture HEIGHT (matches the nightlight,
  // which uses size% of canvas width). size 22 → 22% of H, so text reads at the
  // same visual scale as the nightlight editor instead of ~4× smaller.
  function fontPx(t, H) { return t.size * (H / 100); }
  function fontSpec(t, H) {
    return (t.italic ? "italic " : "") + (t.bold ? "700 " : "400 ") + fontPx(t, H) + "px " + fontCss(t.font);
  }
  function textLines(t) { return String(t.txt).split("\n"); }

  // Shrink a text layer's size so it fits the centre band (u-width 0.5 → ~0.44W
  // with margin) — mirrors the nightlight's autoFitSize: shrink-to-fit on every
  // type / font / style change, never grows back on its own.
  function autoFitText(t) {
    if (!t || !t.txt || t.curved) return;
    var dim = texDims(PREVIEW_W), W = dim.w, H = dim.h;
    t.size = window.KobloText.autoFitSize(tctx, { text: t.txt, bold: t.bold, italic: t.italic, font: t.font,
      size: t.size, fontBasis: H, refWidth: W, maxFrac: 0.44, min: 5 });
  }

  function drawText(ctx, t, W, H) {
    // Canonical glyph render, shared with the nightlight (KobloText.drawLayer).
    // Ornament basis: font scales to texture HEIGHT, curve radius to WIDTH.
    window.KobloText.drawLayer(ctx, t, { W: W, H: H, fontBasis: H, radiusBasis: W });
  }

  // bounding box of a text layer in texture-fraction space (for hit-testing)
  function textBounds(t, W, H) {
    tctx.save(); tctx.font = fontSpec(t, H);
    var lines = textLines(t), fs = fontPx(t, H), wMax = 0;
    for (var i = 0; i < lines.length; i++) wMax = Math.max(wMax, tctx.measureText(lines[i]).width);
    tctx.restore();
    if (t.curved) { wMax *= 1.05; }
    var h = fs * 1.2 * lines.length + (t.curved ? fs * 0.8 : 0);   // matches KobloText.LINE_H
    var cx = t.x * W, cy = t.y * H;
    // Non-curved: box is centred on the anchor with the effective width (widened
    // box or natural width); alignment moves the text within it. Curved text has
    // no width handle, so it keeps hugging its arc.
    var effW = t.curved ? wMax : Math.max((t.boxW || 0) * W, wMax);
    var x0 = t.curved ? (t.align === "left" ? cx : t.align === "right" ? cx - wMax : cx - wMax / 2) : (cx - effW / 2);
    return { x: x0 / W, y: (cy - h / 2) / H, w: effW / W, h: h / H };
  }
  // Natural (unwidened) text width in W-space px — the minimum a widened box can be.
  function naturalTextW(t, W, H) {
    tctx.save(); tctx.font = fontSpec(t, H);
    var lines = textLines(t), wMax = 0;
    for (var i = 0; i < lines.length; i++) wMax = Math.max(wMax, tctx.measureText(lines[i]).width);
    tctx.restore();
    return wMax;
  }

  // ── design canvas ───────────────────────────────────────────────────────
  var stage, dcv, dctx, selCv, sctx2d, marker;
  // Size the face-crop window to fill the design pane at the front-hemisphere
  // aspect (≈1.0974), leaving room for the Front/Back toggle above it. Explicit
  // w/h (not just max-width) so it grows as large as the pane allows but never
  // overflows a shorter viewport. The stage inside stays 200%×100% → undistorted.
  // Desktop: the Front/Back toggle lives at the TOP of the tool-panel column
  // (above the tools). Mobile: it stays above the canvas. Idempotent.
  function placeFaceToggle() {
    var tg = $("orn-face-toggle"); if (!tg) return;
    var panel = $("orn-adj-ctrl"), design = $("orn-mode-pane-design");
    if (!isMobile()) { if (panel && tg.parentNode !== panel) panel.insertBefore(tg, panel.firstChild); }
    else if (design && tg.parentNode !== design) design.insertBefore(tg, design.firstChild);
  }
  function sizeFaceCrop() {
    placeFaceToggle();
    var crop = document.querySelector(".orn-face-crop"), pane = $("orn-mode-pane-design");
    if (!crop || !pane) return;
    var tog = $("orn-face-toggle");
    var togInPane = tog && tog.parentNode === pane;   // only counts against canvas height when above it
    var A = WRAP_ASPECT / 2;   // constant → canvas doesn't resize between 3″ and 6″
    var availW = pane.clientWidth;
    var availH = pane.clientHeight - (togInPane ? tog.offsetHeight : 0) - 22;
    if (availW < 40 || availH < 40) return;
    var w = Math.min(availW, availH * A);
    crop.style.maxWidth = "none";
    crop.style.width = Math.round(w) + "px";
    crop.style.height = Math.round(w / A) + "px";
  }
  function renderDesign() {
    if (!dcv || !stage) return;
    sizeFaceCrop();
    var c = bake(STAGE_W, { view: S.side, noText: true });   // 2D stage shows the side being edited
    // Render the canvas at device-pixel-ratio so the photo stays crisp on 2× (Retina)
    // displays. CSS keeps the on-screen size (100% of the stage); the internal
    // buffer is DPR× larger. Cap DPR at 2 to bound cost.
    var DPR = Math.min(window.devicePixelRatio || 1, 2);
    var cw = Math.round((stage.clientWidth || 600) * DPR), ch = Math.round(cw / WRAP_ASPECT);
    if (dcv.width !== cw || dcv.height !== ch) { dcv.width = cw; dcv.height = ch; selCv.width = cw; selCv.height = ch; }
    dctx.drawImage(c, 0, 0, cw, ch);
    // Text is rasterized HERE, at the stage's own device resolution, rather than
    // baked into the STAGE_W texture and enlarged with it: the design pane grows
    // with the window and easily exceeds STAGE_W (≈3800 device px on a 16" laptop
    // vs a 2048-wide bake), and upscaled glyphs read blurry. The photo layer still
    // comes from the texture — enlarging a photo barely shows, and the per-pixel
    // sphere warp would be far too slow to run at stage resolution every frame.
    paintText(dctx, S.side, cw, ch);
    drawGuides(cw, ch);
    drawSelection(cw, ch);
    positionMarker();
    // 3D only matters in preview mode — skip the heavy wrap bake (with the
    // sphere-correct warp) during design-mode edits so dragging stays smooth.
    if (three && S.mode === "preview") { bake(PREVIEW3D_W, { view: "wrap" }); updateWarmCanvas(); three.texWarm.needsUpdate = true; three.texRaw.needsUpdate = true; renderThree(); }
    updateCartEnabled();
  }
  function drawGuides(cw, ch) {
    // green dashed safe band (theta 60°..120°) — keep faces/text between the lines
    dctx.save();
    var y1 = vOf(Math.PI / 3) * ch, y2 = vOf(2 * Math.PI / 3) * ch;
    dctx.strokeStyle = "rgba(34,197,94,.85)"; dctx.setLineDash([6, 5]); dctx.lineWidth = 1.5;
    dctx.beginPath(); dctx.moveTo(0, y1); dctx.lineTo(cw, y1); dctx.stroke();
    dctx.beginPath(); dctx.moveTo(0, y2); dctx.lineTo(cw, y2); dctx.stroke();
    dctx.restore();
  }
  // Width handles only apply to a selected, non-curved text box.
  function widthHandlesOn(t) { return t && t.txt && !t.curved; }
  // Selection UI is shared with the nightlight (KobloText). Box in device px
  // (selCv is at DPR); uiScale = DPR so handles/✕ stay finger-sized.
  function drawSelection(cw, ch) {
    sctx2d.clearRect(0, 0, cw, ch);
    var t = S.texts[S.sel]; if (!t || !t.txt) return;
    var b = textBounds(t, cw, ch);
    var box = { left: b.x * cw, top: b.y * ch, width: b.w * cw, height: b.h * ch };
    window.KobloText.drawSelection(sctx2d, box, { uiScale: cw / (stage.clientWidth || cw), isMobile: isMobile(), widthHandles: widthHandlesOn(t) });
  }
  function positionMarker() {
    if (!marker) return;
    var on = S.img && S.sc && S.tool === "photo" && S.mode === "design";
    marker.style.display = on ? "block" : "none";
    if (on) {
      marker.style.left = (S.mu * stage.clientWidth) + "px";
      marker.style.top = (S.mv * stage.clientHeight) + "px";
    }
  }

  // ── Warm lit-lithophane mapping (same scheme as the nightlight render) ──
  // Thickness → transmitted light (Beer–Lambert): texel gray → wall thickness
  // → warm-white transmission colour. THIN cream → THICK dark brown, absorption
  // 0.85/mm — identical constants to lithophane-editor.js build3D.
  var warmCanvas = document.createElement("canvas");
  var wctx = warmCanvas.getContext("2d");
  var _warmLUT = null, _warmLUTKey = null;
  function warmLUT() {
    var key = S.size.minT + "|" + S.size.maxT;
    if (_warmLUTKey === key) return _warmLUT;
    var THIN = [255, 224, 168], THICK = [46, 28, 18], ABSORB = 0.85;
    var lut = new Uint8ClampedArray(256 * 3);
    for (var l = 0; l < 256; l++) {
      var t = S.size.minT + (1 - l / 255) * (S.size.maxT - S.size.minT);
      var T = Math.exp(-ABSORB * (t - S.size.minT));
      lut[l * 3] = THICK[0] + (THIN[0] - THICK[0]) * T;
      lut[l * 3 + 1] = THICK[1] + (THIN[1] - THICK[1]) * T;
      lut[l * 3 + 2] = THICK[2] + (THIN[2] - THICK[2]) * T;
    }
    _warmLUT = lut; _warmLUTKey = key;
    return lut;
  }
  function updateWarmCanvas() {
    var W = texCanvas.width, H = texCanvas.height;
    warmCanvas.width = W; warmCanvas.height = H;
    var src = tctx.getImageData(0, 0, W, H), d = src.data;
    var lut = warmLUT();
    for (var i = 0; i < d.length; i += 4) {
      var l = (0.299 * d[i] + 0.587 * d[i + 1] + 0.114 * d[i + 2]) | 0;
      d[i] = lut[l * 3]; d[i + 1] = lut[l * 3 + 1]; d[i + 2] = lut[l * 3 + 2];
    }
    wctx.putImageData(src, 0, 0);
  }

  // ── Three.js preview: textured ball + threaded cap ─────────────────────
  var CAP_ROT = -65 * Math.PI / 180;   // yaw so the KOBLO emboss faces the camera on load (visually calibrated)
  var three = null, three_setZoom = null, three_getZoom = null;
  function initThree() {
    if (three || typeof THREE === "undefined") return;
    var cv = $("orn-3d"); if (!cv) return;
    // preserveDrawingBuffer so the cart/email thumbnail can be captured with
    // toDataURL() at checkout time (without it, the buffer reads back blank and
    // the thumb silently falls back to the flat texture = the raw photo).
    var renderer = new THREE.WebGLRenderer({ canvas: cv, antialias: true, alpha: true, preserveDrawingBuffer: true });
    renderer.setPixelRatio(Math.min(2, window.devicePixelRatio || 1));
    // The tree photo is drawn as a PLANE inside the scene (treePlane below), at
    // the ornament's own depth (z=0) — so a camera dolly scales the tree and the
    // ornament identically and the cap stays pinned to the hook-ring at EVERY
    // zoom. Opaque dark clear shows only until the texture loads.
    renderer.setClearColor(0x14180f, 1);
    var scene = new THREE.Scene();
    var bgC = document.createElement("canvas"); bgC.width = bgC.height = 256;
    var bgx = bgC.getContext("2d");
    var bgg = bgx.createRadialGradient(128, 168, 6, 128, 150, 215);
    bgg.addColorStop(0, "#f8edd0");
    bgg.addColorStop(0.30, "#ecdcbc");
    bgg.addColorStop(0.60, "#c7c1bb");
    bgg.addColorStop(1, "#8d8b90");
    bgx.fillStyle = bgg; bgx.fillRect(0, 0, 256, 256);
    var warmBg = new THREE.CanvasTexture(bgC);   // thumbnail-only backdrop
    var cam = new THREE.PerspectiveCamera(32, 1, 0.1, 100);
    var camDist = 5.3;
    cam.position.set(0, 0.18, camDist);
    function setZoom(d) {
      camDist = Math.max(3.2, Math.min(5.3, d));   // 5.3 = full photo fills the frame; no zooming out past that (avoids gaps)
      cam.position.z = camDist;
      renderThree();
    }
    three_setZoom = setZoom;
    three_getZoom = function () { return camDist; };
    // Nightlight-matched lighting: warm ambient carries the brightness so the
    // transmission colours read true; the low warm directional adds just a
    // hint of form (identical constants to lithophane-editor.js).
    scene.add(new THREE.AmbientLight(0xfff4e6, 0.82));
    var dir = new THREE.DirectionalLight(0xffe6c8, 0.28); dir.position.set(0.4, 1, 2.2); scene.add(dir);

    updateWarmCanvas();
    var texWarm = new THREE.CanvasTexture(warmCanvas);   // lit-lithophane colours
    var texRaw = new THREE.CanvasTexture(texCanvas);     // raw grays drive displacement
    // Crisp texturing: max anisotropy + linear filtering (the texture is NPOT, so
    // mipmaps are off in WebGL1 anyway) — keeps the wrap sharp at grazing angles.
    var maxAniso = (renderer.capabilities.getMaxAnisotropy && renderer.capabilities.getMaxAnisotropy()) || 1;
    [texWarm, texRaw].forEach(function (t) {
      t.anisotropy = maxAniso; t.minFilter = THREE.LinearFilter; t.magFilter = THREE.LinearFilter; t.generateMipmaps = false;
    });
    var group = new THREE.Group();

    var g = geom(S.size);
    // Radius 1 = the smooth INNER sphere; the wall is displaced OUTWARD per
    // texel by the real thickness (dark = thick), exactly like the print — so
    // the ball reads with lithophane depth, reaches its true outer diameter,
    // and relief pokes past the collar where the image is dark (same rule as
    // the nightlight's frame chamfer). Displacement: pos + n·(texel·scale+bias)
    // with white → minT and black → maxT.
    // Relief renders the TRUE wall thickness (RELIEF = 1.0 → full minT..maxT,
    // 0.84–2.52 mm), so the preview matches the printed part. Vertex-based
    // displacement needs enough vertices or text edges "stair-step", so the sphere
    // is tessellated densely (640×432) to keep engraved text crisp at full depth.
    // Bias is set so the THINNEST (brightest) point sits exactly at the collar's
    // outer radius (maxT/R) and everything else bulges OUTWARD from there — so the
    // ball is always ≥ the collar and the top part never protrudes past the ball
    // (even with no image), while dark areas still poke past the collar.
    var RELIEF = 1.0;
    var dScale = (S.size.minT - S.size.maxT) / g.R * RELIEF;   // ≤ 0 (dark → thick → outward)
    var ball = new THREE.Mesh(
      new THREE.SphereGeometry(1, 640, 432, 0, TAU, g.thetaTop, g.thetaBot - g.thetaTop),
      new THREE.MeshStandardMaterial({
        map: texWarm, side: THREE.DoubleSide, metalness: 0.0, roughness: 0.72,
        displacementMap: texRaw,
        displacementScale: dScale,
        displacementBias: S.size.maxT / g.R - dScale
      }));
    group.add(ball);

    // Real printed parts (threaded top, bottom plug, KOBLO cap) extracted from
    // the production 3MF template — the preview shows exactly what prints.
    // Positions are mm in the lithophane frame (+Z = hole); rotate +Z→+Y and
    // scale by 1/R to the unit-sphere space of the ball.
    var R = g.R;
    // Matched to the real lit ornament: the printed top cone + bottom are thin
    // walls, so light passes through — they glow a slightly deeper warm tone
    // than the ball. Only the separate KOBLO cap reads as dimmer neutral gray.
    var glowMat = new THREE.MeshStandardMaterial({ color: 0xd9b489, metalness: 0.0, roughness: 0.72 });
    var capMat = new THREE.MeshStandardMaterial({ color: 0xc6c1ba, metalness: 0.0, roughness: 0.6 });
    var partsUrl = S.size.partsUrl || CFG.partsUrl;
    var capY = S.size.capY || 43.6;
    if (partsUrl) {
      fetch(partsUrl).then(function (r) { return r.json(); }).then(function (data) {
        (data.parts || []).forEach(function (p) {
          var geo = new THREE.BufferGeometry();
          geo.setAttribute("position", new THREE.Float32BufferAttribute(p.pos, 3));
          geo.setIndex(p.idx);
          geo.computeVertexNormals();
          geo.rotateX(-Math.PI / 2);          // +Z (hole axis) → +Y (three.js up)
          geo.scale(1 / R, 1 / R, 1 / R);
          var mesh = new THREE.Mesh(geo, p.name === "cap" ? capMat : glowMat);
          if (p.name === "cap") {
            mesh.position.y = capY / R;                       // screwed onto the collar (per-size)
            mesh.rotation.y = CAP_ROT;                        // KOBLO emboss faces front on load
            if (S.size.capFlip) mesh.rotation.x = Math.PI;    // threads point DOWN (6″ cap models nose-up)
          }
          if (three) three.parts[p.name] = mesh;
          group.add(mesh);
        });
        renderThree();
      }).catch(function (e) { console.warn("[orn] parts load failed", e); });
    }

    group.rotation.y = -Math.PI / 2;
    group.scale.setScalar(S.size.previewScale || 1);   // shrink the ornament vs the tree (per-size)
    // Align every size's TOP (cap top) to the same height so switching size doesn't
    // make it jump — the 3" top matches where the 6" top renders (both "hang" from
    // the same point; the smaller one just sits higher and smaller).
    var capTopOf = function (sz) { return ((sz.capY || 0) + (sz.capHalf || 0)) / (sz.diameter / 2) * (sz.previewScale || 1); };
    var refTop = capTopOf((SIZES.filter(function (s) { return s.key === "signature6"; })[0]) || S.size);
    group.position.y = refTop - capTopOf(S.size);
    scene.add(group);
    three = { renderer: renderer, scene: scene, cam: cam, texWarm: texWarm, texRaw: texRaw, group: group, ball: ball, parts: {}, warmBg: warmBg, treePlane: null,
      faceSide: function (side) { group.rotation.y = -Math.PI / 2 + (side === "back" ? Math.PI : 0); renderThree(); } };
    three.faceSide(S.side);
    // Tree photo as an in-scene plane at z=0 (the ornament's own depth) so it
    // dollies with the ornament — the cap stays pinned to the hook-ring at every
    // zoom. Added to the SCENE (not the group), so rotating the ball leaves the
    // tree still. Positioned so the ring (RING_FX,RING_FY of the photo) sits at
    // the cap's world-y.
    if (CFG.backdropUrl) {
      // Show the ENTIRE photo: size the square plane to fill the square view at the
      // default zoom and centre it on the view (camera sits at y 0.18). z=0 keeps
      // it locked to the ornament through zoom. PLANE ≈ view height at camDist 5.3
      // (FOV 32°) so the whole image is visible edge-to-edge.
      var PLANE = 3.05;
      new THREE.TextureLoader().load(CFG.backdropUrl, function (tex) {
        var pl = new THREE.Mesh(
          new THREE.PlaneGeometry(PLANE, PLANE),
          new THREE.MeshBasicMaterial({ map: tex, depthTest: false, depthWrite: false }));
        pl.position.set(0, 0.18, 0);   // centred on the view
        pl.renderOrder = -1;           // always behind the ornament
        scene.add(pl);
        if (three) { three.treePlane = pl; renderThree(); }
      });
    }
    window.__ornThree = three;   // debug hook (harmless in production)
    resizeThree();
    var drag = false, lx = 0, ly = 0, pvPointers = {}, pvPinch = null;
    cv.addEventListener("pointerdown", function (e) {
      pvPointers[e.pointerId] = { x: e.clientX, y: e.clientY };
      var ids = Object.keys(pvPointers);
      if (ids.length === 2) {
        var a = pvPointers[ids[0]], b = pvPointers[ids[1]];
        pvPinch = { d0: Math.hypot(a.x - b.x, a.y - b.y), z0: camDist };
        drag = false;
        return;
      }
      drag = true; lx = e.clientX; ly = e.clientY; cv.setPointerCapture(e.pointerId);
    });
    cv.addEventListener("pointermove", function (e) {
      if (pvPointers[e.pointerId]) pvPointers[e.pointerId] = { x: e.clientX, y: e.clientY };
      if (pvPinch) {
        var ids = Object.keys(pvPointers);
        if (ids.length >= 2) {
          var a = pvPointers[ids[0]], b = pvPointers[ids[1]];
          var d = Math.hypot(a.x - b.x, a.y - b.y);
          setZoom(pvPinch.z0 * (pvPinch.d0 / Math.max(1, d)));
        }
        return;
      }
      if (!drag) return;
      // Spin around the vertical axis only — no up/down tilt (group.rotation.x
      // stays fixed) so the ornament always hangs level like on a tree.
      group.rotation.y += (e.clientX - lx) * 0.01;
      lx = e.clientX; ly = e.clientY; renderThree();
    });
    function pvEnd(e) {
      delete pvPointers[e.pointerId];
      if (pvPinch && Object.keys(pvPointers).length < 2) pvPinch = null;
      drag = false;
    }
    cv.addEventListener("pointerup", pvEnd);
    cv.addEventListener("pointercancel", pvEnd);
    cv.addEventListener("wheel", function (e) {
      e.preventDefault();
      setZoom(camDist * (e.deltaY > 0 ? 1.07 : 0.93));
    }, { passive: false });
    var zi = $("orn-3d-zin"), zo = $("orn-3d-zout");
    if (zi) zi.onclick = function () { setZoom(camDist * 0.85); };
    if (zo) zo.onclick = function () { setZoom(camDist * 1.18); };
    var load = $("orn-prev-load"); if (load) load.style.display = "none";
    renderThree();
  }
  function resizeThree() {
    if (!three) return;
    var wrap = $("orn-preview-wrap"); if (!wrap) return;
    var w = wrap.clientWidth || 400, h = wrap.clientHeight || w;
    three.renderer.setSize(w, h, false);
    three.cam.aspect = w / h; three.cam.updateProjectionMatrix();
    renderThree();
  }
  function renderThree() { if (three) three.renderer.render(three.scene, three.cam); }
  // Tear down the 3D so the next preview rebuilds it — needed when the size
  // changes (sphere geometry + parts differ per size). Cloning the canvas drops
  // every listener initThree stacked on it, so a rebuild can't double-bind.
  function disposeThree() {
    if (!three) return;
    try { three.renderer.dispose(); } catch (e) {}
    var cv = $("orn-3d");
    if (cv && cv.parentNode) cv.parentNode.replaceChild(cv.cloneNode(false), cv);
    three = null;
  }

  // ── history ─────────────────────────────────────────────────────────────
  var HIST = [], HI = -1;
  function snapshot() {
    return JSON.stringify({ zoom: S.zoom, offX: S.offX, offY: S.offY, sc: S.sc,
      scStrength: S.scStrength, mu: S.mu, mv: S.mv, bgFront: S.bgFront, bgBack: S.bgBack, side: S.side,
      frontTexts: frontTextsNow(), backTexts: backTextsNow(), sel: S.sel, hasImg: !!S.img });
  }
  var _imgStack = [];  // parallel storage of image refs (not JSON-able)
  function pushHistory() {
    HIST = HIST.slice(0, HI + 1); _imgStack = _imgStack.slice(0, HI + 1);
    HIST.push(snapshot());
    _imgStack.push({ img: S.img, natW: S.natW, natH: S.natH, photoData: S.photoData });
    HI = HIST.length - 1; updateUndoRedo();
    // A genuine user change (not the initial edit-load seeding) marks the design
    // dirty so "Save Design" enables.
    if (S.editJobId && !S.hydrating) S.editDirty = true;
    S.previewReady = false; updateCartEnabled();   // a design change invalidates the rendered preview
  }
  function restore(i) {
    var s = JSON.parse(HIST[i]), im = _imgStack[i];
    S.zoom = s.zoom; S.offX = s.offX; S.offY = s.offY; S.sc = s.sc; S.scStrength = s.scStrength;
    S.mu = s.mu; S.mv = s.mv;
    S.bgFront = s.bgFront || s.bg || "gray"; S.bgBack = s.bgBack || "gray"; S.sel = s.sel;
    S.side = s.side || "front";
    var sf = s.frontTexts || s.texts || [null, null], sbk = s.backTexts || [null, null];
    if (S.side === "front") { S.texts = sf; S.backTexts = sbk; }
    else { S.texts = sbk; S.frontTexts = sf; }
    S.img = im.img; S.natW = im.natW; S.natH = im.natH; S.photoData = im.photoData;
    S.previewReady = false;
    syncSideUI(); syncAll(); renderDesign();
  }
  function undo() { if (HI > 0) { HI--; restore(HI); updateUndoRedo(); } }
  function redo() { if (HI < HIST.length - 1) { HI++; restore(HI); updateUndoRedo(); } }
  function updateUndoRedo() {
    [["orn-undo-btn", HI <= 0], ["orn-top-undo", HI <= 0],
     ["orn-redo-btn", HI >= HIST.length - 1], ["orn-top-redo", HI >= HIST.length - 1]]
      .forEach(function (p) { var el = $(p[0]); if (el) el.disabled = p[1]; });
  }

  // ── UI sync ──────────────────────────────────────────────────────────────
  function lwRoot() { return document.querySelector("#orn-shell .lw"); }
  function syncAll() { syncPhotoUI(); syncTextUI(); syncSizeUI(); }
  function syncPhotoUI() {
    // Keep the upload prompt hidden while an edit is loading — otherwise it flashes
    // on top of the loading overlay before the saved photo arrives.
    var cu = $("orn-canvas-upload"); if (cu) cu.style.display = (S.hydrating || S.side === "back" || S.img || hasFrontText()) ? "none" : "";
    var pc = $("orn-photo-controls"); if (pc) pc.style.display = S.img ? "block" : "none";
    var z = $("orn-zoom"); if (z) z.value = S.zoom;
    var sc = $("orn-sc-toggle"); if (sc) sc.checked = S.sc;
    var sr = $("orn-sc-strength-row"); if (sr) sr.style.display = S.sc ? "" : "none";
    var st = $("orn-sc-strength"); if (st) st.value = Math.round(S.scStrength * 100);
    var wz = $("orn-warn-zoom"); if (wz) wz.classList.toggle("on", zoomTooFar());
    var we = $("orn-warn-editor"); if (we) we.classList.toggle("on", isLowRes());
    // Front bg swatches reflect bgFront; the block shows in the Photo tool ONLY
    // when the photo doesn't fill the whole face (otherwise there's nothing to fill).
    ["extend", "white", "gray", "dark"].forEach(function (b) {
      var el = $("orn-bg-" + b); if (el) el.classList.toggle("active-sw", S.bgFront === b);
    });
    var fg = $("orn-fg-block"); if (fg) fg.style.display = (S.side === "front" && photoHasGaps()) ? "" : "none";
    // Back bg swatches (solid only) reflect bgBack; the bar shows only on the back.
    if (S.bgBack === "extend") S.bgBack = "gray";   // back never uses the photo blur
    ["white", "gray", "dark"].forEach(function (b) {
      var el = $("orn-bgb-" + b); if (el) el.classList.toggle("active-sw", S.bgBack === b);
    });
    var bb = $("orn-bg-bar-back"); if (bb) bb.style.display = (S.side === "back") ? "" : "none";
  }
  function hasTextIn(arr) { return arr.some(function (t) { return t && t.txt; }); }
  function hasFrontText() { return hasTextIn(frontTextsNow()); }
  function hasAnyText() { return hasTextIn(frontTextsNow()) || hasTextIn(backTextsNow()); }
  function isLowRes() { return !!S.img && Math.max(S.natW, S.natH) < (PHOTODEF.lowResMinPx || 700); }
  function zoomTooFar() {
    if (!S.img) return false;
    var d = texDims(EXPORT_W), p = placement(d.w, d.h);
    return (p.w / S.natW) > 2.2;   // export texels per source px
  }
  function syncSizeUI() {
    var btns = document.querySelectorAll("[data-orn-size]");
    Array.prototype.forEach.call(btns, function (b) {
      b.classList.toggle("active", b.getAttribute("data-orn-size") === S.size.key);
    });
    var pr = $("orn-cart-price");
    if (pr && S.size.price) pr.textContent = " · " + S.size.price;
  }
  function grpEls(n) {
    return {
      input: $(n === 1 ? "orn-text-input" : "orn-text-input2"),
      count: $("orn-count-" + n),
      font: $("orn-font-select-" + n),
      sizeVal: $("orn-size" + n + "-val"),
      bold: $("orn-style" + n + "-bold"), italic: $("orn-style" + n + "-italic"), underline: $("orn-style" + n + "-underline"),
      alignL: $("orn-align" + n + "-left"), alignC: $("orn-align" + n + "-center"), alignR: $("orn-align" + n + "-right"),
      dark: $("orn-text" + n + "-dark"), light: $("orn-text" + n + "-light"),
      outline: $("orn-outline" + n + "-toggle"),
      curve: $(n === 1 ? "orn-curve-toggle" : "orn-curve-toggle2"),
      curveStep: $(n === 1 ? "orn-curve-stepper" : "orn-curve-stepper2"),
      curveVal: $(n === 1 ? "orn-curve-value" : "orn-curve-value2")
    };
  }
  function syncTextUI() {
    var root = lwRoot(); if (!root) return;
    if (!isMobile()) {
      root.classList.remove("lw-text-editing");
      var te0 = $("orn-top-editor"); if (te0) te0.classList.remove("on");
    }
    root.classList.toggle("lw-text-selected", S.sel >= 0);
    root.classList.toggle("lw-sel-text1", S.sel === 0);
    root.classList.toggle("lw-sel-text2", S.sel === 1);
    // Mobile: hide the top undo/redo/help row while a text is selected (the bottom
    // sheet owns the text controls; the row just crowds the small screen).
    if (isMobile()) {
      var ta = $("orn-top-actions"); if (ta) ta.style.visibility = (S.sel >= 0) ? "hidden" : "";
    }
    // list of existing texts in the add panel
    var list = $("orn-text-list");
    if (list) {
      list.innerHTML = "";
      S.texts.forEach(function (t, i) {
        if (!t) return;
        var row = document.createElement("div"); row.className = "lw-text-list-row";
        var item = document.createElement("button"); item.type = "button"; item.className = "lw-text-list-item";
        item.textContent = t.txt || "(empty text)";
        item.onclick = function () { selectText(i); };
        var del = document.createElement("button"); del.type = "button"; del.className = "lw-text-list-del";
        del.innerHTML = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M3 6h18M8 6V4h8v2M6 6l1 14h10l1-14"/></svg>';
        del.onclick = function () { deleteText(i); };
        row.appendChild(item); row.appendChild(del); list.appendChild(row);
      });
    }
    var addBtn = $("orn-add-text-btn");
    if (addBtn) addBtn.style.display = (S.texts[0] && S.texts[1]) ? "none" : "";
    for (var n = 1; n <= 2; n++) {
      var t = S.texts[n - 1]; if (!t) continue;
      var E = grpEls(n);
      if (E.input && E.input.value !== t.txt) E.input.value = t.txt;
      if (E.count) setCharNotice(E.count, t.txt.length);
      if (E.font) E.font.value = t.font;
      if (E.sizeVal) E.sizeVal.textContent = Math.round(t.size);
      if (E.bold) E.bold.classList.toggle("active", t.bold);
      if (E.italic) E.italic.classList.toggle("active", t.italic);
      if (E.underline) E.underline.classList.toggle("active", t.underline);
      [["alignL", "left"], ["alignC", "center"], ["alignR", "right"]].forEach(function (p) {
        if (E[p[0]]) E[p[0]].classList.toggle("active-dark", t.align === p[1]);
      });
      if (E.dark) E.dark.classList.toggle("active-sw", t.color !== "light");
      if (E.light) E.light.classList.toggle("active-sw", t.color === "light");
      if (E.outline) E.outline.checked = t.outline;
      if (E.curve) E.curve.checked = t.curved;
      if (E.curveStep) E.curveStep.style.display = t.curved ? "flex" : "none";
      if (E.curveVal) E.curveVal.textContent = Math.round(t.curveAmount);
    }
    updateTextGating();
  }
  // Formatting gating (nightlight parity): multi-line text can't curve (hide the
  // Curved control); Curved text disables Underline and locks alignment to centre.
  function updateTextGating() {
    for (var n = 1; n <= 2; n++) {
      var t = S.texts[n - 1]; if (!t) continue;
      var sfx = n === 1 ? "" : "2";
      // Shared with the nightlight — mutates t.curved/t.align in place.
      window.KobloText.applyGating(t, {
        curveRow: $("orn-curve-toggle-row" + sfx), curveToggle: $("orn-curve-toggle" + sfx), curveStepper: $("orn-curve-stepper" + sfx),
        underlineBtn: $("orn-style" + n + "-underline"),
        alignBtns: { left: $("orn-align" + n + "-left"), center: $("orn-align" + n + "-center"), right: $("orn-align" + n + "-right") }
      });
    }
  }
  // Enabled whenever there's designable content. The Preview requirement is
  // enforced in onAddToCart (first click jumps to Preview with a toast) rather
  // than by graying the button out.
  function updateCartEnabled() {
    var btn = $("orn-cart-btn"); if (!btn) return;
    // Truly disabled only when there's nothing to save (or an untouched edit).
    btn.disabled = !(S.img || hasAnyText()) || (S.editJobId && !S.editDirty);
    // Grey it (but keep it clickable) until the design has been previewed, so any
    // change re-greys it; clicking while greyed shows the "preview first" message.
    btn.classList.toggle("lw-needs-preview", !btn.disabled && !S.previewReady);
  }

  // ── text layer ops ───────────────────────────────────────────────────────
  function addText() {
    // Photo-first: no text-only ornaments. The FRONT needs a photo before text
    // (the back is always text-only, so it's exempt).
    if (S.side === "front" && !S.img) {
      setTool("photo");
      if (window.KOBLO && KOBLO.toast) KOBLO.toast("Add your photo first");
      return;
    }
    // A slot is "free" if it's empty OR holds a text with no content — so a
    // re-add always drops a FRESH centred layer (never inherits a stale, moved
    // position from a text that was deleted/cleared).
    function slotFree(i) { return !S.texts[i] || !S.texts[i].txt; }
    var slot = slotFree(0) ? 0 : (slotFree(1) ? 1 : -1);
    if (slot < 0) return;
    var t = newText(slot === 0 ? 0.62 : 0.78);
    // Back text lives on the narrower back hemisphere, so start it smaller and
    // centred so it fits the back zone without spilling toward the front.
    t.size = S.side === "back" ? (slot === 0 ? 15 : 12) : (slot === 0 ? 18 : 14);
    if (S.side === "back") { t.y = slot === 0 ? 0.5 : 0.64; }
    t.txt = DEFAULT_TEXT;
    autoFitText(t);   // shrink the placeholder to fit the band (shared KobloText.autoFitSize)
    // Auto-contrast: sample the baked texture where the text will land — on a
    // dark photo/extend region, dark text would be invisible, so start light.
    try {
      bake(PREVIEW_W, { view: S.side });
      var W = texCanvas.width, H = texCanvas.height;
      var sw = Math.round(W * 0.24), sh = Math.round(H * 0.12);
      var sx = Math.round(t.x * W - sw / 2), sy = Math.round(t.y * H - sh / 2);
      sx = Math.max(0, Math.min(W - sw, sx)); sy = Math.max(0, Math.min(H - sh, sy));
      var d = tctx.getImageData(sx, sy, sw, sh).data, sum = 0, n = 0;
      for (var i = 0; i < d.length; i += 16) { sum += 0.299 * d[i] + 0.587 * d[i + 1] + 0.114 * d[i + 2]; n++; }
      if (sum / n < 120) t.color = "light";
    } catch (e) {}
    S.texts[slot] = t;
    selectText(slot);
    var cu = $("orn-canvas-upload"); if (cu) cu.style.display = "none";
    // Select the placeholder so the first keystroke replaces it (both platforms).
    if (isMobile()) { openTopEditor(); var ti = $("orn-top-text-editor"); if (ti) { try { ti.select(); } catch (e) {} } }
    else { var E = grpEls(slot + 1); if (E.input) { E.input.focus(); try { E.input.select(); } catch (e2) {} } }
    pushHistory();
    ensureReserved();   // pre-mint the SKU on text too, so add-to-cart never waits on the mint (nightlight parity)
  }
  function selectText(i) { S.sel = i; setTool("text"); syncTextUI(); renderDesign(); }
  function deselectText() { S.sel = -1; hideInlineEd(); closeTopEditor(); renderDesign(); }   // tapping off also dismisses the mobile keyboard/inline editor
  function deleteText(i) {
    S.texts[i] = null; if (S.sel === i) S.sel = -1;
    syncAll(); renderDesign(); pushHistory();
  }

  // ── front / back sides ─────────────────────────────────────────────────
  // S.texts always holds the ACTIVE side's layers; the inactive side is parked
  // in frontTexts/backTexts. These resolve each side no matter which is active.
  function frontTextsNow() { return S.side === "front" ? S.texts : S.frontTexts; }
  function backTextsNow() { return S.side === "back" ? S.texts : S.backTexts; }
  function syncSideUI() {
    var fb = $("orn-side-front"), bb = $("orn-side-back");
    if (fb) { fb.classList.toggle("active", S.side === "front"); fb.setAttribute("aria-selected", S.side === "front"); }
    if (bb) { bb.classList.toggle("active", S.side === "back"); bb.setAttribute("aria-selected", S.side === "back"); }
    var pt = $("orn-tool-photo");             // photo tool is front-only
    if (pt) pt.style.display = (S.side === "back") ? "none" : "";
    if (three) three.faceSide(S.side);        // rotate the 3D to the edited face
  }
  function setSide(side) {
    if (side !== "front" && side !== "back") return;
    if (side === S.side) return;
    if (S.side === "front") S.frontTexts = S.texts; else S.backTexts = S.texts;
    S.side = side;
    S.texts = (side === "front") ? S.frontTexts : S.backTexts;
    S.sel = -1; closeTopEditor();   // switching face deselects → dismiss the editor
    setTool(side === "back" ? "text" : "photo");
    syncSideUI(); syncAll(); renderDesign();
  }

  // ── tools / modes / mobile nav ───────────────────────────────────────────
  function setTool(tool) {
    if (tool !== "text") closeTopEditor();   // leaving the Text tool dismisses the editor
    S.tool = tool;
    var pp = $("orn-tool-pane-photo"), tp = $("orn-tool-pane-text"), sp = $("orn-tool-pane-size");
    if (pp) pp.classList.toggle("on", tool === "photo");
    if (tp) tp.classList.toggle("on", tool === "text");
    if (sp) sp.classList.toggle("on", tool === "size");
    var bp = $("orn-tool-photo"), bt = $("orn-tool-text"), bs = $("orn-tool-size");
    if (bp) bp.classList.toggle("active", tool === "photo");
    if (bt) bt.classList.toggle("active", tool === "text");
    if (bs) bs.classList.toggle("active", tool === "size");
    var mp = $("orn-mnav-photo"), mt = $("orn-mnav-text");
    if (mp) mp.classList.toggle("active", tool === "photo" && S.mode === "design");
    if (mt) mt.classList.toggle("active", tool === "text" && S.mode === "design");
    if (tool !== "text") deselectText(); else syncTextUI();
    positionMarker();
  }
  function setMode(m) {
    if (m !== "design") closeTopEditor();   // leaving the canvas dismisses the mobile text editor/keyboard
    S.mode = m;
    var d = $("orn-mode-pane-design"), p = $("orn-mode-pane-preview");
    if (d) d.classList.toggle("on", m === "design");
    if (p) p.classList.toggle("on", m === "preview");
    var bd = $("orn-mode-design"), bp = $("orn-mode-preview");
    if (bd) bd.classList.toggle("active", m === "design");
    if (bp) bp.classList.toggle("active", m === "preview");
    var ws = $("orn-workspace"); if (ws) ws.classList.toggle("lw-preview-mode", m === "preview");
    var rt = lwRoot(); if (rt) rt.classList.toggle("lw-preview", m === "preview");   // mobile: show the title row
    // Undo/Redo don't apply on the Preview — hide them there (nightlight parity).
    ["orn-undo-btn", "orn-redo-btn", "orn-top-undo", "orn-top-redo"].forEach(function (id) {
      var e = $(id); if (e) e.style.display = (m === "preview") ? "none" : "";
    });
    var mv = $("orn-mnav-preview"); if (mv) mv.classList.toggle("active", m === "preview");
    // Mobile nav: the tool tabs are only "active" in design mode — clear them on
    // Preview so the last-used tool doesn't stay highlighted.
    var mnp = $("orn-mnav-photo"), mnt = $("orn-mnav-text");
    if (mnp) mnp.classList.toggle("active", S.tool === "photo" && m === "design");
    if (mnt) mnt.classList.toggle("active", S.tool === "text" && m === "design");
    if (m === "preview") {
      bake(PREVIEW3D_W); initThree(); resizeThree();   // high-res texture for a crisp 3D preview
      if (three) { three.group.rotation.set(0, -Math.PI / 2, 0);   // always open facing the front (ignore any prior shopper spin)
        updateWarmCanvas(); three.texWarm.needsUpdate = true; three.texRaw.needsUpdate = true; renderThree(); }
      S.previewReady = true; updateCartEnabled();   // seeing the 3D result unlocks Add to Cart
    }
    else renderDesign();
    positionMarker();
  }

  // mobile top text editor
  function openTopEditor() {
    if (!isMobile()) return;
    var te = $("orn-top-editor"), inp = $("orn-top-text-editor");
    var t = S.texts[S.sel]; if (!te || !inp || !t) return;
    lwRoot().classList.add("lw-text-editing");
    te.classList.add("on"); inp.value = t.txt; inp.focus();
    // The top-editor ✓ done sits where the shell ✕ close is; hide the ✕ while
    // editing so they don't overlap (matches the nightlight).
    var c = $("orn-close"); if (c) c.style.visibility = "hidden";
    updateTopCount();
  }
  function closeTopEditor() {
    var te = $("orn-top-editor"); if (te) te.classList.remove("on");
    var root = lwRoot(); if (root) root.classList.remove("lw-text-editing");
    var c = $("orn-close"); if (c) c.style.visibility = "";
    syncTextUI();
  }

  // ── Desktop: inline on-canvas text editor (double-click to type on the text) ──
  var _inlineEd = null;
  function ensureInlineEd() {
    if (_inlineEd) return _inlineEd;
    var ta = document.createElement("textarea");
    ta.className = "orn-inline-edit"; ta.maxLength = 40; ta.rows = 1; ta.setAttribute("spellcheck", "false");
    ta.addEventListener("input", function () {
      if (S.sel < 0) return; var t = S.texts[S.sel]; if (!t) return;
      t.txt = ta.value; autoFitText(t);
      var E = grpEls(S.sel + 1); if (E.input) E.input.value = ta.value;   // keep the toolbar input in sync
      syncTextUI(); renderDesign(); positionInlineEd();
    });
    ta.addEventListener("blur", function () { hideInlineEd(); pushHistory(); });
    ta.addEventListener("keydown", function (e) { if (e.key === "Escape" || (e.key === "Enter" && !e.shiftKey)) { e.preventDefault(); ta.blur(); } });
    var crop = document.querySelector(".orn-face-crop"); if (crop) crop.appendChild(ta);
    _inlineEd = ta; return ta;
  }
  function positionInlineEd() {
    var ta = _inlineEd; if (!ta || ta.style.display === "none" || S.sel < 0) return;
    var t = S.texts[S.sel]; if (!t) return;
    var crop = document.querySelector(".orn-face-crop"); if (!crop) return;
    ta.style.left = ((t.x - FRONT_U0) / (FRONT_U1 - FRONT_U0) * crop.clientWidth) + "px";   // crop shows u∈[.25,.75]
    ta.style.top = (t.y * crop.clientHeight) + "px";
  }
  function openInlineEd() {
    if (isMobile() || S.sel < 0) return;
    var t = S.texts[S.sel]; if (!t) return;
    var ta = ensureInlineEd();
    ta.value = t.txt; ta.style.display = "block"; positionInlineEd();
    ta.focus(); try { ta.select(); } catch (e) {}
  }
  function hideInlineEd() { if (_inlineEd) _inlineEd.style.display = "none"; }
  function updateTopCount() {
    // Live "n/40" counter in the mobile top editor (matches the nightlight); the
    // in-pane counters stay silent-until-limit via setCharNotice.
    var c = $("orn-top-editor-count"), inp = $("orn-top-text-editor");
    if (!c || !inp) return;
    var len = inp.value.length;
    c.textContent = len + "/" + MAX_CHARS;
    c.classList.toggle("lw-char-max", len >= MAX_CHARS);
  }

  // ── photo load / rotate ──────────────────────────────────────────────────
  // S.origImg is the un-rotated upload; S.img (+ photoData) is derived from it
  // via applyRotation(), so both the drawImage and sphere-correct sampling paths
  // work on the rotated pixels transparently. Rotation is never sent to the
  // backend (it's baked into the texture) but IS saved so edits rehydrate right.
  function applyRotation() {
    if (!S.origImg) { S.img = null; S.photoData = null; S.natW = S.natH = 0; return; }
    var r = ((S.rotation % 360) + 360) % 360;
    var ow = S.origImg.naturalWidth || S.origImg.width, oh = S.origImg.naturalHeight || S.origImg.height;
    if (r === 0) { S.img = S.origImg; S.natW = ow; S.natH = oh; }
    else {
      var swp = (r === 90 || r === 270), cw = swp ? oh : ow, ch = swp ? ow : oh;
      var c = document.createElement("canvas"); c.width = cw; c.height = ch;
      var x = c.getContext("2d");
      x.translate(cw / 2, ch / 2); x.rotate(r * Math.PI / 180); x.drawImage(S.origImg, -ow / 2, -oh / 2);
      S.img = c; S.natW = cw; S.natH = ch;
    }
    var oc = document.createElement("canvas"); oc.width = S.natW; oc.height = S.natH;
    var octx = oc.getContext("2d"); octx.drawImage(S.img, 0, 0);
    try { S.photoData = octx.getImageData(0, 0, S.natW, S.natH).data; } catch (e) { S.photoData = null; }
  }
  function rotateBy(deg) {
    if (!S.origImg) return;
    S.rotation = (((S.rotation + deg) % 360) + 360) % 360;
    applyRotation(); syncAll(); renderDesign(); pushHistory();
  }
  function loadFile(file) {
    if (!file) return;
    var im = new Image();
    im.onload = function () {
      S.origImg = im; S.rotation = 0;
      applyRotation();
      S.cleanBlob = file; S.editCleanExt = null;   // fresh upload replaces any edit-preserved original
      S.zoom = 1; S.offX = 0; S.offY = 0; S.mu = 0.5; S.mv = vEquator(); S.lowResAccepted = false;
      S.sc = false; S.scStrength = 1;   // sphere-correct is an opt-in toggle; always centred at 100% when on
      syncAll(); renderDesign(); pushHistory();
      if (isLowRes()) { var m = $("orn-lowres-modal"); if (m) m.classList.add("on"); }
      ensureReserved();
      uploadOriginalInBackground(file);
    };
    im.src = URL.createObjectURL(file);
  }
  function removePhoto() {
    S.img = null; S.photoData = null; S.origImg = null; S.rotation = 0; S.natW = S.natH = 0; S.cleanBlob = null; S.cleanName = null; S.editCleanExt = null;
    S.sc = false;
    syncAll(); renderDesign(); pushHistory();
  }
  function doRestart() {
    S.img = null; S.photoData = null; S.origImg = null; S.rotation = 0; S.natW = S.natH = 0; S.cleanBlob = null; S.cleanName = null; S.editCleanExt = null;
    S.zoom = 1; S.offX = 0; S.offY = 0; S.sc = false; S.scStrength = 1; S.bgFront = "gray"; S.bgBack = "gray";
    S.mu = 0.5; S.mv = vEquator(); S.sel = -1; S.lowResAccepted = false; S.previewReady = false;
    S.side = "front"; S.texts = [null, null]; S.frontTexts = [null, null]; S.backTexts = [null, null];
    setTool("photo"); setMode("design"); syncSideUI();
    HIST = []; _imgStack = []; HI = -1;
    syncAll(); renderDesign(); pushHistory();
  }
  function restartDesign(skipConfirm) {
    if (skipConfirm === true) { doRestart(); return; }
    KOBLO.confirm({
      title: T("restartTitle", "Restart your design?"),
      body: T("restartBody", "This clears your photo and text."),
      confirmText: T("restartConfirm", "Restart"), cancelText: T("restartCancel", "Keep editing"), danger: true
    }).then(function (ok) { if (ok) doRestart(); });
  }

  // ── backend protocol: reserve → save → cart ─────────────────────────────
  function ensureReserved() {
    if (S.jobId || S.reserving || !API) return;
    S.reserving = fetch(API + "/reserve", {
      method: "POST",
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
      body: "product=ornament&size=" + encodeURIComponent(S.size.key)
    }).then(function (r) { return r.json(); }).then(function (j) {
      if (j.job_id) { S.jobId = j.job_id; S.variantId = j.variant_id; }
      S.reserving = null;
    }).catch(function () { S.reserving = null; });
  }
  function uploadOriginalInBackground(file) {
    if (!API) return;
    // Versioned draft name: in EDIT mode S.jobId is the existing job, so a fixed
    // clean.<ext> key would OVERWRITE the saved design's photo the moment a new
    // one is chosen — even if the shopper abandons the edit. /save records which
    // file is current (meta.clean_key); unsaved drafts are just orphans.
    var ext = ((file.name || "photo.jpg").split(".").pop() || "jpg").toLowerCase();
    S.cleanName = "clean-" + Math.random().toString(36).slice(2, 10) + "." + ext;
    var send = function () {
      if (!S.jobId) return;
      var fd = new FormData();
      fd.append("job_id", S.jobId);
      fd.append("clean_name", S.cleanName);
      fd.append("original", file, file.name || "photo.jpg");
      fetch(API + "/save-original", { method: "POST", body: fd }).catch(function () {});
    };
    if (S.jobId) send(); else if (S.reserving) S.reserving.then(send);
  }

  function setStatus(msg, cls) {
    var el = $("orn-status"); if (!el) return;
    el.textContent = msg || ""; el.className = "lw-header-status" + (cls ? " " + cls : "");
  }

  function dataURItoBlob(d) {
    var mime = (d.split(",")[0].match(/data:([^;]+)/) || [])[1] || "image/png";
    var b = atob(d.split(",")[1]), arr = new Uint8Array(b.length);
    for (var i = 0; i < b.length; i++) arr[i] = b.charCodeAt(i);
    return new Blob([arr], { type: mime });
  }

  // Proceed enables when the design is approved AND (no photo OR rights checked).
  function updateConfirmProceed() {}   // button stays clickable; approval is enforced in proceedCheckout with a message
  // Little popover anchored to the Add-to-Cart button, so the "preview first"
  // nudge appears right where the user clicked (not a detached toast).
  function showCartHint(text) {
    var btn = $("orn-cart-btn"); if (!btn) return;
    var hint = $("orn-cart-hint");
    if (!hint) { hint = document.createElement("div"); hint.id = "orn-cart-hint"; hint.className = "orn-cart-hint"; document.body.appendChild(hint); }
    hint.textContent = text; hint.style.display = "block";
    var r = btn.getBoundingClientRect();
    // Flip above the button when there's no room below (mobile: the cart button
    // sits at the bottom of the screen, so "below" would be off-screen).
    var below = r.bottom + 12 + hint.offsetHeight <= window.innerHeight - 8;
    hint.classList.toggle("orn-cart-hint--above", !below);
    hint.style.top = (below ? r.bottom + 10 : r.top - hint.offsetHeight - 10) + "px";
    hint.style.left = Math.max(8, Math.min(window.innerWidth - hint.offsetWidth - 8, r.right - hint.offsetWidth)) + "px";
    clearTimeout(hint._t); hint._t = setTimeout(function () { hint.style.display = "none"; }, 6000);
  }
  function hideCartHint() { var h = $("orn-cart-hint"); if (h) h.style.display = "none"; }
  function onAddToCart() {
    if (!(S.img || hasAnyText())) return;
    // First take them to the 3D preview (unless they've already seen it), so
    // they review the real render before buying — with the nudge shown AT the button.
    if (!S.previewReady) {
      setMode("preview");
      var lbl = S.editJobId ? T("saveDesign", "Save Design") : T("addToCart", "Add to Cart");
      var tail = S.editJobId ? T("previewTailSave", "to update your design.") : T("previewTailAdd", "to add it to your cart.");
      var nudge = T("previewNudge", "👀 Here’s your 3D preview — click “{label}” again {tail}").replace("{label}", lbl).replace("{tail}", tail);
      showCartHint(nudge);
      return;
    }
    hideCartHint();
    var m = $("orn-confirm-modal"); if (!m) return;
    var f = $("orn-confirm-form"), l = $("orn-confirm-loading");
    if (f) f.style.display = ""; if (l) l.style.display = "none";
    var cd = $("orn-confirm-design");
    if (cd) cd.checked = false;
    updateConfirmProceed();
    m.classList.add("on");
  }

  function proceedCheckout() {
    var cd = $("orn-confirm-design");
    if (!(cd && cd.checked)) {   // require the approval box, with a message (not a greyed button)
      if (window.KOBLO && KOBLO.toast) KOBLO.toast(T("approveRequired", "Please check the box to confirm you approve your design."));
      return;
    }
    var f = $("orn-confirm-form"), l = $("orn-confirm-loading");
    if (f) f.style.display = "none"; if (l) l.style.display = "block";
    var msg = $("orn-confirm-loading-msg");
    var retry = $("orn-confirm-retry"); if (retry) retry.style.display = "none";

    // full-res texture (photo + sphere-correct + text baked) = the production
    // image. JPEG (like the nightlight) — a full-detail PNG here is many MB and
    // trips a 413 at the edge; q0.95 keeps the luminance the relief is carved from.
    var textureURL = (function () { bake(EXPORT_W); return texCanvas.toDataURL("image/jpeg", 0.95); })();
    // Cart/email thumb = the warm 3D ornament render (falls back to the flat
    // texture if WebGL is unavailable). Captured square at 560px.
    var thumb = (function () {
      try {
        bake(PREVIEW3D_W); initThree();   // high-res so the cart thumbnail/lightbox is crisp
        if (three) {
          updateWarmCanvas();
          three.texWarm.needsUpdate = true; three.texRaw.needsUpdate = true;
          // Cart thumb: tree backdrop + the ornament, front-facing (KOBLO forward)
          // regardless of how the shopper rotated. Zoom so the ornament fills the
          // thumb the SAME for every size (the 3" zooms in to match the 6"), and
          // centre the camera on the ornament. 700px JPEG q0.85 stays under
          // werkzeug's 500KB form-memory cap.
          var pr0 = three.renderer.getPixelRatio();
          var cp = { x: three.cam.position.x, y: three.cam.position.y, z: three.cam.position.z };
          var rp = { x: three.group.rotation.x, y: three.group.rotation.y };
          var ps = S.size.previewScale || 1;
          var capTopU = (S.size.capY + (S.size.capHalf || 0)) / (S.size.diameter / 2);
          var camY = three.group.position.y + ps * (capTopU - 1) / 2;   // vertical centre of the ornament
          three.cam.position.set(0, camY, 5.3 * (ps / 0.75));            // 6"=5.3; 3" zooms to match
          three.group.rotation.x = 0; three.group.rotation.y = -Math.PI / 2;   // KOBLO front (ignore shopper rotation)
          three.renderer.setPixelRatio(1);
          three.renderer.setSize(700, 700, false);
          three.cam.aspect = 1; three.cam.updateProjectionMatrix();
          three.renderer.render(three.scene, three.cam);
          var url = three.renderer.domElement.toDataURL("image/jpeg", 0.85);
          three.cam.position.set(cp.x, cp.y, cp.z);
          three.group.rotation.x = rp.x; three.group.rotation.y = rp.y;
          three.renderer.setPixelRatio(pr0);
          resizeThree();
          if (url && url.length > 2000) return url;
        }
      } catch (e) { console.warn("[orn] 3D thumb failed, using flat", e); }
      var d = texDims(512); var c = document.createElement("canvas");
      c.width = d.w; c.height = d.h;
      c.getContext("2d").drawImage(bake(512), 0, 0);
      return c.toDataURL("image/jpeg", 0.85);
    })();

    var doSave = function () {
      var fd = new FormData();
      fd.append("product", "ornament");
      fd.append("orn_size", S.size.key);
      fd.append("image", dataURItoBlob(textureURL), "texture.jpg");
      if (S.jobId && S.variantId) { fd.append("job_id", S.jobId); fd.append("variant_id", S.variantId); }
      if (S.cleanBlob) fd.append("clean_pre_uploaded", S.cleanName || (S.cleanBlob.name || "photo.jpg").split(".").pop().toLowerCase());
      else if (S.editCleanExt) fd.append("clean_pre_uploaded", S.editCleanExt);   // preserve the uncropped original across edits (filename or legacy ext)
      fd.append("design_name", ($("orn-design-name") || { value: "" }).value || "");
      fd.append("preview", thumb);
      // placement + text settings for re-edit / regeneration
      var meta = { zoom: S.zoom, offX: S.offX, offY: S.offY, rotation: S.rotation, sc: S.sc, scStrength: S.scStrength,
        mu: S.mu, mv: S.mv, size: S.size.key, side: S.side,
        texts: frontTextsNow(), frontTexts: frontTextsNow(), backTexts: backTextsNow(),
        hasImg: !!S.img, bgFront: S.bgFront, bgBack: S.bgBack };
      fd.append("orn_settings", JSON.stringify(meta));
      if (S.editJobId) fd.append("edit_job_id", S.editJobId);
      return fetch(API + "/save", { method: "POST", body: fd }).then(function (r) {
        if (!r.ok) throw new Error("save failed (" + r.status + ")");
        return r.json();
      });
    };

    var btn = $("orn-cart-btn");
    var setLoad = function (t) { if (msg) msg.textContent = t; setStatus(t); };
    var sub = $("orn-confirm-loading-sub"); if (sub) sub.textContent = T("pleaseWait", "Please don’t close or refresh this page.");
    if (btn) btn.disabled = true;
    setLoad(T("saving", "Saving your design…"));

    var saved, cartData = null;
    doSave().then(function (j) {
      saved = j;
      S.jobId = j.job_id; S.variantId = j.variant_id;
      // Debug hooks: CFG.debugDownload downloads then continues to the cart;
      // naming the design BAKTEST downloads INSTEAD of adding to cart. BAKTEST
      // takes only the 3MF — that's the file that actually goes to the printer;
      // debugDownload still takes the raw STL alongside it.
      var bakTest = ((($("orn-design-name") || {}).value) || "").trim().toUpperCase() === "BAKTEST";
      if (CFG.debugDownload || bakTest) {
        setLoad(bakTest ? "Generating test 3MF (about 2 minutes)…"
                        : "Generating test STL + 3MF (about 2 minutes)…");
        return fetch(API + "/generate-ornament/" + j.job_id, { method: "POST" })
          .then(function (r) { return r.json(); })
          .then(function (g) {
            var withStl = !bakTest && !!g.stl_url;
            if (withStl) triggerDownload(g.stl_url, S.jobId + ".stl");
            // Stagger only when two downloads fire back to back.
            if (g.mf_url) setTimeout(function () { triggerDownload(g.mf_url, S.jobId + ".3mf"); },
                                     withStl ? 800 : 0);
          }).catch(function (e) { console.warn("[orn] debug generate failed", e); })
          .then(function () { if (bakTest) throw { __baktest: true }; });   // skip the cart
      }
    }).then(function () {
      setLoad(S.editCartLine ? T("updatingCart", "Updating cart…") : T("addingToCart", "Adding to cart…"));
      var vid = (saved.variant_id || saved.default_variant_id || "").split("/").pop();
      if (!vid) throw new Error("no variant");
      if (!/myshopify\.com$|koblogoods\.com$/.test(location.hostname)) {
        console.log("[orn] dev host — skipping real cart add", saved); return null;
      }
      var name = (($("orn-design-name") || {}).value || "").trim().slice(0, 40);
      var previewUrl = (saved.preview_url || (API + "/preview/" + S.jobId)).replace(/^http:/, "https:");
      var previewProp = previewUrl + (previewUrl.indexOf("?") >= 0 ? "&" : "?") + "v=" + Date.now();
      var designImg = API + "/design/" + S.jobId + "?v=" + Date.now();
      var ftx = frontTextsNow(), btx = backTextsNow();
      var t1 = ftx[0] && ftx[0].txt ? ftx[0].txt : "";
      var t2 = ftx[1] && ftx[1].txt ? ftx[1].txt : "";
      var tBack = [btx[0], btx[1]].map(function (t) { return t && t.txt ? t.txt : ""; }).filter(Boolean).join(" / ");
      var props = {
        "_job_id": S.jobId,
        "_preview": previewProp,
        "_design_image": designImg,
        "_text_line_1": t1,
        "_text_line_2": t2,
        "_text_back": tBack,
        // Visible (no leading _) so the chosen size shows in the cart like a normal
        // variant option, e.g. "Size: Signature 6″".
        "Size": S.size.label
      };
      if (name) props["Design name"] = name;   // only show it when the shopper entered one (nightlight parity)
      // On an edit the SKU is reused — remove the old cart line FIRST so re-adding
      // the same variant doesn't merge into it.
      var pre = S.editCartLine
        ? fetch("/cart/change.js", { method: "POST", headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ id: S.editCartLine, quantity: 0 }) })
        : Promise.resolve();
      return pre.then(function () {
        return fetch("/cart/add.js", {
          method: "POST", headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            items: [{ id: parseInt(vid, 10), quantity: 1, properties: props }],
            sections: "cart-drawer,cart-icon-bubble",
            sections_url: window.location.pathname
          })
        }).then(function (r) { if (!r.ok) throw new Error("Cart failed: " + r.status); return r.json(); });
      });
    }).then(function (cj) {
      cartData = cj;
      // Update the cart drawer + count bubble in place from the returned sections
      // (no page reload — matches the nightlight).
      try {
        var sec = (cj && cj.sections) || {};
        if (sec["cart-drawer"]) {
          var doc = new DOMParser().parseFromString(sec["cart-drawer"], "text/html");
          var ni = doc.getElementById("CartDrawer"), oi = document.getElementById("CartDrawer");
          var no = doc.querySelector("cart-drawer"), oo = document.querySelector("cart-drawer");
          if (ni && oi) oi.innerHTML = ni.innerHTML;
          if (no && oo) oo.className = no.className;
        }
        if (sec["cart-icon-bubble"]) {
          var bub = document.getElementById("cart-icon-bubble");
          if (bub) bub.innerHTML = sec["cart-icon-bubble"];
        }
      } catch (e) { console.error("cart section update failed", e); }
      fetch("/cart.js").then(function (r) { return r.json(); }).then(function (c) {
        document.querySelectorAll("[data-cart-count]").forEach(function (el) { el.textContent = c.item_count; });
      }).catch(function () {});

      var okMsg = S.editCartLine ? T("designUpdated", "Design updated! ✓") : T("addedToCart", "Added to cart! ✓");
      setLoad(okMsg); setStatus(okMsg, "ok");
      if (btn) btn.disabled = false;
      var m = $("orn-confirm-modal"); if (m) m.classList.remove("on");
      exitAfterEdit();   // cart-page edits → /cart; drawer/fresh → reopen drawer + reset to fresh
    }).catch(function (e) {
      if (e && e.__baktest) {
        var bMsg = "BAKTEST: 3MF downloaded — nothing was added to your cart.";
        setLoad(bMsg); setStatus(bMsg, "ok");
        if (btn) btn.disabled = false;
        var bm = $("orn-confirm-modal"); if (bm) bm.classList.remove("on");
        return;
      }
      console.error(e);
      var sp = $("orn-confirm-loading"); // keep loading view but swap to error + retry
      if (msg) msg.textContent = T("cartError", "⚠️ Couldn’t add to cart");
      if (sub) sub.textContent = e.message;
      if (retry) retry.style.display = "";
      setStatus("Error: " + e.message, "err");
      if (btn) btn.disabled = false;
    });
  }
  function triggerDownload(url, name) {
    var a = document.createElement("a");
    a.href = url; a.download = name || ""; a.rel = "noopener";
    document.body.appendChild(a); a.click(); a.remove();
  }

  // ── canvas interactions (photo drag/zoom, text select/drag/resize) ──────
  function canvasPos(e) {
    var r = stage.getBoundingClientRect();
    return { x: (e.clientX - r.left) / r.width, y: (e.clientY - r.top) / r.height };
  }
  function hitText(pos) {
    var cw = stage.clientWidth, ch = stage.clientHeight;
    for (var i = S.texts.length - 1; i >= 0; i--) {
      var t = S.texts[i]; if (!t || !t.txt) continue;
      var b = textBounds(t, cw, ch);
      if (pos.x >= b.x - 0.01 && pos.x <= b.x + b.w + 0.01 && pos.y >= b.y - 0.01 && pos.y <= b.y + b.h + 0.01) return i;
    }
    return -1;
  }
  function hitHandle(pos) {
    var t = S.texts[S.sel]; if (!t || !t.txt) return null;
    var cw = stage.clientWidth, ch = stage.clientHeight;   // CSS px → uiScale 1
    var b = textBounds(t, cw, ch);
    var box = { left: b.x * cw, top: b.y * ch, width: b.w * cw, height: b.h * ch };
    var px = pos.x * cw, py = pos.y * ch;
    var opts = { uiScale: 1, isMobile: isMobile(), widthHandles: widthHandlesOn(t) };
    if (window.KobloText.hitTestDelete(box, px, py, opts)) return "del";
    var h = window.KobloText.hitTestHandle(box, px, py, opts);
    return h ? h.corner : null;
  }

  function wireCanvas() {
    var pdrag = null;       // {kind:'photo'|'text'|'resize', ...}
    var pinch = null;       // two-pointer zoom
    var pointers = {};

    stage.addEventListener("pointerdown", function (e) {
      pointers[e.pointerId] = { x: e.clientX, y: e.clientY };
      var ids = Object.keys(pointers);
      if (ids.length === 2) {
        var a = pointers[ids[0]], b = pointers[ids[1]];
        pinch = { d0: Math.hypot(a.x - b.x, a.y - b.y), zoom0: S.zoom,
                  size0: S.sel >= 0 && S.texts[S.sel] ? S.texts[S.sel].size : 0 };
        pdrag = null;
        return;
      }
      var pos = canvasPos(e);
      if (S.mode !== "design") return;
      var handle = S.sel >= 0 ? hitHandle(pos) : null;
      if (handle === "del") { deleteText(S.sel); return; }
      if (handle) {
        var t = S.texts[S.sel], cw = stage.clientWidth, ch = stage.clientHeight;
        var bb = textBounds(t, cw, ch);
        if (handle === "lm" || handle === "rm") {
          // Width drag: keep the opposite edge fixed, widen toward the cursor.
          pdrag = { kind: "resize", handle: handle, width: true,
            fixedX: handle === "rm" ? bb.x : bb.x + bb.w, natW: naturalTextW(t, cw, ch) / cw };
        } else {
          pdrag = { kind: "resize", handle: handle, size0: t.size,
            cx: bb.x + bb.w / 2, cy: bb.y + bb.h / 2, d0: Math.hypot(pos.x - (bb.x + bb.w / 2), pos.y - (bb.y + bb.h / 2)) };
        }
        stage.setPointerCapture(e.pointerId);
        return;
      }
      var ti = hitText(pos);
      if (ti >= 0) {
        var tt = S.texts[ti];
        // Nightlight parity: 1st tap SELECTS (handles + tools); a drag just
        // repositions; the keyboard opens only on a clean 2nd tap of an
        // already-selected text (handled in endPointer) — never on pointerdown,
        // so moving a selected text can't pop the editor.
        var wasSel = (S.sel === ti);
        if (!wasSel) selectText(ti);
        else if (S.tool !== "text") setTool("text");   // clicking an already-selected text still surfaces the Text tool
        pdrag = { kind: "text", idx: ti, x0: pos.x, y0: pos.y, tx0: tt.x, ty0: tt.y, moved: false, wasSel: wasSel };
        stage.setPointerCapture(e.pointerId);
        return;
      }
      if (S.sel >= 0) deselectText();
      if (S.img) {
        pdrag = { kind: "photo", x0: e.clientX, y0: e.clientY, ox0: S.offX, oy0: S.offY };
        _draggingPhoto = true;   // show the flat photo while positioning; warp snaps back on release
        stage.setPointerCapture(e.pointerId);
      }
    });
    stage.addEventListener("pointermove", function (e) {
      if (pointers[e.pointerId]) pointers[e.pointerId] = { x: e.clientX, y: e.clientY };
      // Hover cursor (match nightlight): corner handles resize, ✕ delete = pointer,
      // over text = move, over photo = grab. Without this the stage keeps a stale
      // grab cursor over the size handles / delete button.
      if (!pdrag && !pinch && S.mode === "design") {
        var hp = canvasPos(e), hh = S.sel >= 0 ? hitHandle(hp) : null, cur;
        if (hh === "del") cur = "pointer";
        else if (hh === "tl" || hh === "br") cur = "nwse-resize";
        else if (hh === "tr" || hh === "bl") cur = "nesw-resize";
        else if (hh === "lm" || hh === "rm") cur = "ew-resize";
        else if (hitText(hp) >= 0) cur = "move";
        else cur = S.img ? "grab" : "default";
        stage.style.cursor = cur;
      }
      if (pinch) {
        var ids = Object.keys(pointers);
        if (ids.length >= 2) {
          var a = pointers[ids[0]], b = pointers[ids[1]];
          var d = Math.hypot(a.x - b.x, a.y - b.y), k = d / (pinch.d0 || 1);
          if (S.sel >= 0 && S.texts[S.sel]) { S.texts[S.sel].size = Math.max(5, Math.min(60, pinch.size0 * k)); syncTextUI(); }
          else if (S.img) { S.zoom = Math.max(PHOTODEF.zoomMin, Math.min(PHOTODEF.zoomMax, pinch.zoom0 * k)); _draggingPhoto = true; syncPhotoUI(); }
          renderDesign();
        }
        return;
      }
      if (!pdrag) return;
      var pos = canvasPos(e);
      if (pdrag.kind === "photo") {
        stage.style.cursor = "grabbing";
        var k = PREVIEW_W / stage.clientWidth;
        S.offX = pdrag.ox0 + (e.clientX - pdrag.x0) * k;
        S.offY = pdrag.oy0 + (e.clientY - pdrag.y0) * k;
        showPhotoSnap();
        renderDesign();
      } else if (pdrag.kind === "text") {
        var t = S.texts[pdrag.idx]; if (!t) return;
        t.x = Math.max(0, Math.min(1, pdrag.tx0 + (pos.x - pdrag.x0)));
        t.y = Math.max(0.03, Math.min(0.97, pdrag.ty0 + (pos.y - pdrag.y0)));
        pdrag.moved = true;
        showSnap(t); renderDesign();
      } else if (pdrag.kind === "resize") {
        var tt = S.texts[S.sel]; if (!tt) return;
        if (pdrag.width) {
          // Widen the box toward the cursor, keeping the opposite edge fixed and
          // re-centring the anchor — never smaller than the natural text width.
          var newW = Math.min(1, Math.max(pdrag.natW, Math.abs(pos.x - pdrag.fixedX)));
          var newCx = pdrag.handle === "rm" ? pdrag.fixedX + newW / 2 : pdrag.fixedX - newW / 2;
          tt.boxW = newW; tt.x = Math.max(0, Math.min(1, newCx));
        } else {
          var d2 = Math.hypot(pos.x - pdrag.cx, pos.y - pdrag.cy);
          tt.size = Math.max(5, Math.min(60, pdrag.size0 * (d2 / (pdrag.d0 || 0.001))));
        }
        syncTextUI(); renderDesign();
      }
    });
    function endPointer(e) {
      delete pointers[e.pointerId];
      var wasDraggingPhoto = _draggingPhoto;
      if (pinch && Object.keys(pointers).length < 2) { pinch = null; pushHistory(); }
      if (pdrag) {
        if (pdrag.kind === "photo" || pdrag.kind === "resize" || (pdrag.kind === "text" && pdrag.moved)) pushHistory();
        // Clean 2nd tap on an already-selected text → open the keyboard (mobile).
        // A drag (moved) or a first-tap select never opens it.
        if (pdrag.kind === "text" && !pdrag.moved && pdrag.wasSel && isMobile()) openTopEditor();
        pdrag = null; hideSnap();
      }
      // photo positioning done → re-apply the sphere-correct warp (was skipped live)
      if (wasDraggingPhoto && Object.keys(pointers).length === 0) { _draggingPhoto = false; renderDesign(); }
    }
    stage.addEventListener("pointerup", endPointer);
    stage.addEventListener("pointercancel", endPointer);

    // Desktop: double-click a text to edit its letters right on the canvas.
    stage.addEventListener("dblclick", function (e) {
      if (isMobile() || S.mode !== "design") return;
      var ti = hitText(canvasPos(e));
      if (ti >= 0) { selectText(ti); openInlineEd(); }
    });

    var wheelT = null;
    stage.addEventListener("wheel", function (e) {
      if (!S.img || S.mode !== "design") return;
      e.preventDefault();
      S.zoom = Math.max(PHOTODEF.zoomMin, Math.min(PHOTODEF.zoomMax, S.zoom * (e.deltaY < 0 ? 1.06 : 0.94)));
      syncPhotoUI(); renderDesign();
      if (wheelT) clearTimeout(wheelT); wheelT = setTimeout(pushHistory, 400);
    }, { passive: false });

    // drag & drop upload
    stage.addEventListener("dragover", function (e) { e.preventDefault(); });
    stage.addEventListener("drop", function (e) {
      e.preventDefault();
      if (e.dataTransfer && e.dataTransfer.files[0]) loadFile(e.dataTransfer.files[0]);
    });
  }
  // Text snap: horizontal centre (x=0.5) + three vertical targets — the safe-band
  // top (θ=60°), the equator, and the safe-band bottom (θ=120°). Snaps the value
  // and shows the matching guide line (nightlight parity).
  function showSnap(t) {
    var sh = $("orn-sh"), sv = $("orn-sv");
    var nearV = Math.abs(t.x - 0.5) < 0.012;
    if (nearV) t.x = 0.5;
    var yTargets = [vOf(Math.PI / 3), vEquator(), vOf(2 * Math.PI / 3)];
    var snapY = null;
    for (var i = 0; i < yTargets.length; i++) {
      if (Math.abs(t.y - yTargets[i]) < 0.014) { t.y = yTargets[i]; snapY = yTargets[i]; break; }
    }
    if (sv) { sv.style.display = nearV ? "block" : "none"; sv.style.left = "50%"; }
    if (sh) { if (snapY != null) { sh.style.display = "block"; sh.style.top = (snapY * 100) + "%"; } else sh.style.display = "none"; }
  }
  // Photo snap: centre the photo on the canvas (offX/offY → 0) when dragged near.
  function showPhotoSnap() {
    // Snap window in SCREEN pixels (see SNAP_PX) converted into offX/offY units,
    // which are stored at PREVIEW_W scale — so the pull is the same felt distance
    // here as on the nightlight regardless of how wide the stage is drawn.
    var sh = $("orn-sh"), sv = $("orn-sv");
    var stageW = (stage && stage.clientWidth) || PREVIEW_W;
    var thr = SNAP_PX * (PREVIEW_W / stageW);
    var nx = Math.abs(S.offX) < thr, ny = Math.abs(S.offY) < thr;
    if (nx) S.offX = 0;
    if (ny) S.offY = 0;
    if (sv) { sv.style.display = nx ? "block" : "none"; sv.style.left = "50%"; }
    if (sh) { sh.style.display = ny ? "block" : "none"; sh.style.top = "50%"; }
  }
  function hideSnap() {
    var sh = $("orn-sh"), sv = $("orn-sv");
    if (sh) sh.style.display = "none"; if (sv) sv.style.display = "none";
  }

  // ── marker drag ──────────────────────────────────────────────────────────
  function wireMarker() {
    if (!marker) return;
    var mdrag = false;
    marker.addEventListener("pointerdown", function (e) { e.stopPropagation(); mdrag = true; marker.setPointerCapture(e.pointerId); });
    marker.addEventListener("pointermove", function (e) {
      if (!mdrag) return;
      var r = stage.getBoundingClientRect();
      S.mu = Math.max(0, Math.min(1, (e.clientX - r.left) / r.width));
      S.mv = Math.max(0, Math.min(1, (e.clientY - r.top) / r.height));
      renderDesign();
    });
    marker.addEventListener("pointerup", function () { if (mdrag) { mdrag = false; pushHistory(); } });
  }

  // ── per-layer control wiring ─────────────────────────────────────────────
  function wireTextGroup(n) {
    var i = n - 1, E = grpEls(n);
    function T() { return S.texts[i]; }
    function upd(fn, commit) { var t = T(); if (!t) return; fn(t); syncTextUI(); renderDesign(); if (commit !== false) pushHistory(); }
    if (E.input) {
      E.input.addEventListener("input", function () { upd(function (t) { t.txt = E.input.value; autoFitText(t); }, false); });
      E.input.addEventListener("change", pushHistory);
    }
    if (E.font) E.font.addEventListener("change", function () { upd(function (t) { t.font = E.font.value; autoFitText(t); }); });
    var dn = $("orn-size" + n + "-dn"), up = $("orn-size" + n + "-up");
    if (dn) dn.onclick = function () { upd(function (t) { t.size = Math.max(5, t.size - 1); }); };
    if (up) up.onclick = function () { upd(function (t) { t.size = Math.min(60, t.size + 1); }); };
    if (E.bold) E.bold.onclick = function () { upd(function (t) { t.bold = !t.bold; }); };
    if (E.italic) E.italic.onclick = function () { upd(function (t) { t.italic = !t.italic; }); };
    if (E.underline) E.underline.onclick = function () { upd(function (t) { t.underline = !t.underline; }); };
    if (E.alignL) E.alignL.onclick = function () { upd(function (t) { t.align = "left"; }); };
    if (E.alignC) E.alignC.onclick = function () { upd(function (t) { t.align = "center"; }); };
    if (E.alignR) E.alignR.onclick = function () { upd(function (t) { t.align = "right"; }); };
    if (E.dark) E.dark.onclick = function () { upd(function (t) { t.color = "dark"; }); };
    if (E.light) E.light.onclick = function () { upd(function (t) { t.color = "light"; }); };
    if (E.outline) E.outline.addEventListener("change", function () { upd(function (t) { t.outline = E.outline.checked; }); });
    if (E.curve) E.curve.addEventListener("change", function () { upd(function (t) { t.curved = E.curve.checked; }); });
    var cdn = $(n === 1 ? "orn-curve-dn" : "orn-curve-dn2"), cup = $(n === 1 ? "orn-curve-up" : "orn-curve-up2");
    if (cdn) cdn.onclick = function () { upd(function (t) { t.curveAmount = Math.max(10, (t.curveAmount || 10) - 10); }); };
    if (cup) cup.onclick = function () { upd(function (t) { t.curveAmount = Math.min(100, (t.curveAmount || 10) + 10); }); };
    var cflip = $(n === 1 ? "orn-curve-flip" : "orn-curve-flip2");
    if (cflip) cflip.onclick = function () { upd(function (t) { t.curveFlip = !t.curveFlip; }); };
    var del = $("orn-text-del-" + n); if (del) del.onclick = function () { deleteText(i); };
  }

  // ── boot / wiring ────────────────────────────────────────────────────────
  function populateFonts() {
    [1, 2].forEach(function (n) {
      var sel = $("orn-font-select-" + n); if (!sel) return;
      sel.innerHTML = "";
      FONTS.forEach(function (f) {
        var o = document.createElement("option");
        o.value = f.name; o.textContent = f.name; o.style.fontFamily = f.css;
        sel.appendChild(o);
      });
    });
  }

  function openModal() {
    var sh = $("orn-shell");
    if (sh) { sh.classList.add("open"); document.body.classList.add("lw-shell-lock"); }
    setTimeout(function () { renderDesign(); }, 30);
  }
  // Plain close: hide shell, unlock scroll, strip edit params from the URL.
  function closeShell() {
    var sh = $("orn-shell");
    if (sh) { sh.classList.remove("open"); document.body.classList.remove("lw-shell-lock"); }
    if (location.search.indexOf("edit=") >= 0) {
      var u = new URL(location.href);
      u.searchParams.delete("edit"); u.searchParams.delete("cart_line"); u.searchParams.delete("from");
      history.replaceState({}, "", u.toString());
    }
  }
  function openCartDrawer() {
    document.dispatchEvent(new CustomEvent("cart:open"));
    var b = document.getElementById("cart-icon-bubble");
    if (b) b.click();
  }
  // After finishing an edit, wipe edit state so "Create My Design" starts fresh.
  function clearEditToFresh() {
    S.editJobId = null; S.editCartLine = null; S.editFrom = null;
    S.jobId = null; S.variantId = null; S.reserving = null;
    var dn = $("orn-design-name"); if (dn) dn.value = "";
    var lbl = $("orn-cart-label"); if (lbl) lbl.textContent = T("addToCart", "Add to Cart");
    var pr = $("orn-cart-price"); if (pr) pr.style.display = "";
    var cp = $("orn-confirm-proceed"); if (cp) cp.textContent = T("addToCart", "Add to Cart");
    setStatus("");
    S.previewReady = false;
    restartDesign(true);           // blank the design (skip the confirm)
  }
  // Route the exit by where the shopper opened the edit from: cart PAGE → back to
  // /cart; cart DRAWER (default) → reopen the drawer, then reset to a fresh design.
  function exitAfterEdit() {
    if (S.editFrom === "cart") { window.location.href = "/cart"; return; }
    closeShell(); openCartDrawer(); clearEditToFresh();
  }
  // Leaving an EDIT via ✕/backdrop used to silently discard changes — confirm
  // first. (A fresh design keeps its in-editor state after closing, so no
  // warning is needed there.)
  function confirmDiscardEdit(onDiscard) {
    var ov = $("orn-editexit-ov");
    if (!ov) {
      ov = document.createElement("div");
      ov.id = "orn-editexit-ov";
      ov.style.cssText = "position:fixed;inset:0;z-index:1000002;background:rgba(0,0,0,.45);display:flex;align-items:center;justify-content:center;padding:20px";
      ov.innerHTML = '<div style="background:#fff;border-radius:14px;max-width:340px;width:100%;padding:20px 18px;box-shadow:0 12px 40px rgba(0,0,0,.25)">' +
        '<div style="font-weight:700;font-size:16px;margin-bottom:6px;color:#2c332f">Save your changes?</div>' +
        '<div style="font-size:13.5px;color:#555;line-height:1.45;margin-bottom:14px">You’ve made changes to this design that aren’t saved yet.</div>' +
        '<div style="display:flex;flex-direction:column;gap:8px">' +
        '<button type="button" id="orn-editexit-save" style="padding:11px;border-radius:9px;border:0;background:#2c332f;color:#fff;font-weight:600;cursor:pointer">Save Design</button>' +
        '<button type="button" id="orn-editexit-discard" style="padding:11px;border-radius:9px;border:1px solid #d8d4d4;background:#fff;color:#b03a2e;font-weight:600;cursor:pointer">Discard Changes</button>' +
        '<button type="button" id="orn-editexit-back" style="padding:11px;border-radius:9px;border:1px solid #d8d4d4;background:#fff;color:#2c332f;cursor:pointer">Keep Editing</button>' +
        '</div></div>';
      document.body.appendChild(ov);
    }
    ov.style.display = "flex";
    var hide = function () { ov.style.display = "none"; };
    ov.onclick = function (e) { if (e.target === ov) hide(); };
    $("orn-editexit-back").onclick = hide;
    $("orn-editexit-discard").onclick = function () { hide(); onDiscard(); };
    // Save = the normal Save Design flow (incl. the preview-first gate).
    $("orn-editexit-save").onclick = function () { hide(); var b = $("orn-cart-btn"); if (b) b.click(); };
  }
  // X / backdrop: if editing, confirm then route by origin; otherwise just close.
  function closeModal() {
    if (S.editJobId && S.editDirty) confirmDiscardEdit(exitAfterEdit);
    else if (S.editJobId) exitAfterEdit();
    else closeShell();
  }
  window.openOrnamentModal = openModal;

  function wire() {
    stage = $("orn-stage"); dcv = $("orn-cv"); selCv = $("orn-sel-cv"); marker = $("orn-marker");
    if (!stage || !dcv) return;
    dctx = dcv.getContext("2d"); sctx2d = selCv.getContext("2d");
    populateFonts();

    var close = $("orn-close"); if (close) close.onclick = closeModal;
    var bd = $("orn-backdrop"); if (bd) bd.onclick = closeModal;

    // upload paths
    var file = $("orn-file");
    if (file) file.addEventListener("change", function (e) { loadFile(e.target.files[0]); file.value = ""; });
    // The WHOLE dropzone opens the picker (not just the icon/text) — matches the
    // nightlight. The "add text only" button stops propagation so it's exempt.
    var cuZone = $("orn-canvas-upload");
    if (cuZone) {
      cuZone.onclick = function () { if (file) file.click(); };
      cuZone.ondragover = function (e) { e.preventDefault(); cuZone.classList.add("over"); };
      cuZone.ondragleave = function () { cuZone.classList.remove("over"); };
      cuZone.ondrop = function (e) {
        e.preventDefault(); cuZone.classList.remove("over");
        if (e.dataTransfer && e.dataTransfer.files[0]) loadFile(e.dataTransfer.files[0]);
      };
    }
    // (text-only "no photo" entry removed — ornaments are photo-first)
    [["orn-change-photo"], ["orn-m-change-photo"]].forEach(function (p) {
      var b = $(p[0]); if (b) b.onclick = function () { if (file) file.click(); };
    });
    var clr = $("orn-clear"); if (clr) clr.onclick = removePhoto;

    // zoom
    var zoom = $("orn-zoom");
    if (zoom) {
      zoom.addEventListener("input", function () { S.zoom = parseFloat(zoom.value); renderDesign(); syncPhotoUI(); });
      zoom.addEventListener("change", pushHistory);
    }
    var zdn = $("orn-z-dn"); if (zdn) zdn.onclick = function () { S.zoom = Math.max(PHOTODEF.zoomMin, S.zoom * 0.92); syncPhotoUI(); renderDesign(); pushHistory(); };
    var zup = $("orn-z-up"); if (zup) zup.onclick = function () { S.zoom = Math.min(PHOTODEF.zoomMax, S.zoom * 1.08); syncPhotoUI(); renderDesign(); pushHistory(); };
    var reset = $("orn-reset"); if (reset) reset.onclick = function () { S.zoom = 1; S.offX = 0; S.offY = 0; syncPhotoUI(); renderDesign(); pushHistory(); };
    var mreset = $("orn-m-reset"); if (mreset) mreset.onclick = function () { S.zoom = 1; S.offX = 0; S.offY = 0; syncPhotoUI(); renderDesign(); pushHistory(); };
    var rotL = $("orn-rot-l"); if (rotL) rotL.onclick = function () { rotateBy(-90); };
    var rotR = $("orn-rot-r"); if (rotR) rotR.onclick = function () { rotateBy(90); };

    // background fill — front swatches (in the Photo tool, incl. Blur) drive
    // bgFront; back swatches (solid only) drive bgBack.
    ["extend", "white", "gray", "dark"].forEach(function (b) {
      var el = $("orn-bg-" + b);
      if (el) el.onclick = function () { S.bgFront = b; syncPhotoUI(); renderDesign(); pushHistory(); };
    });
    ["white", "gray", "dark"].forEach(function (b) {
      var el = $("orn-bgb-" + b);
      if (el) el.onclick = function () { S.bgBack = b; syncPhotoUI(); renderDesign(); pushHistory(); };
    });

    // sphere-correct
    var sc = $("orn-sc-toggle");
    if (sc) sc.addEventListener("change", function () { S.sc = sc.checked; syncPhotoUI(); positionMarker(); renderDesign(); pushHistory(); });
    var scst = $("orn-sc-strength");
    if (scst) { scst.addEventListener("input", function () { S.scStrength = parseInt(scst.value, 10) / 100; renderDesign(); }); scst.addEventListener("change", pushHistory); }

    // sizes
    Array.prototype.forEach.call(document.querySelectorAll("[data-orn-size]"), function (b) {
      b.addEventListener("click", function () {
        var key = b.getAttribute("data-orn-size");
        var sz = SIZES.filter(function (s) { return s.key === key; })[0];
        if (!sz || sz.stub || key === S.size.key) return;
        S.size = sz;
        // The reserved variant is size-specific — a stale reservation would add the
        // wrong variant/price to the cart. Drop it and re-reserve for the new size.
        S.jobId = null; S.variantId = null; S.reserving = null;
        if (S.img || (S.texts && S.texts.some(function (t) { return t && t.txt; }))) ensureReserved();
        // Sphere geometry + printed parts differ per size — force the 3D to rebuild.
        S.previewReady = false; disposeThree();
        syncSizeUI();
        if (S.mode === "preview") {
          // Already on the Preview: rebuild the 3D in place (renderDesign alone
          // would leave the disposed scene blank).
          bake(PREVIEW3D_W); initThree(); resizeThree();
          if (three) { three.group.rotation.set(0, -Math.PI / 2, 0);
            updateWarmCanvas(); three.texWarm.needsUpdate = true; three.texRaw.needsUpdate = true; renderThree(); }
          S.previewReady = true; updateCartEnabled();
        } else renderDesign();
        pushHistory();
      });
    });

    // tools & modes
    var tp = $("orn-tool-photo"); if (tp) tp.onclick = function () { setTool("photo"); };
    var tsz = $("orn-tool-size"); if (tsz) tsz.onclick = function () { setTool("size"); };
    var tt = $("orn-tool-text"); if (tt) tt.onclick = function () { setTool("text"); deselectText(); };   // Text tab → back to the add/list overview
    var sf = $("orn-side-front"); if (sf) sf.onclick = function () { setSide("front"); };
    var sb = $("orn-side-back"); if (sb) sb.onclick = function () { setSide("back"); };
    var md = $("orn-mode-design"); if (md) md.onclick = function () { setMode("design"); };
    var mp = $("orn-mode-preview"); if (mp) mp.onclick = function () { setMode("preview"); };

    // mobile nav
    var mnp = $("orn-mnav-photo"); if (mnp) mnp.onclick = function () { setMode("design"); setTool("photo"); };
    var mnt = $("orn-mnav-text"); if (mnt) mnt.onclick = function () { setMode("design"); setTool("text"); deselectText(); };
    var mnv = $("orn-mnav-preview"); if (mnv) mnv.onclick = function () { setMode("preview"); };
    var mnr = $("orn-mnav-restart"); if (mnr) mnr.onclick = restartDesign;

    // undo/redo/restart/help
    [["orn-undo-btn", undo], ["orn-top-undo", undo], ["orn-redo-btn", redo], ["orn-top-redo", redo]]
      .forEach(function (p) { var el = $(p[0]); if (el) el.onclick = p[1]; });
    var rs = $("orn-restart-design"); if (rs) rs.onclick = restartDesign;
    [["orn-help-btn"], ["orn-top-help"]].forEach(function (p) {
      var b = $(p[0]); if (b) b.onclick = function () { var m = $("orn-help-modal"); if (m) m.classList.add("on"); };
    });
    var hc = $("orn-help-close"), hg = $("orn-help-gotit");
    if (hc) hc.onclick = function () { $("orn-help-modal").classList.remove("on"); };
    if (hg) hg.onclick = function () { $("orn-help-modal").classList.remove("on"); };

    // low-res modal
    var lrc = $("orn-lowres-change"), lra = $("orn-lowres-accept");
    if (lrc) lrc.onclick = function () { $("orn-lowres-modal").classList.remove("on"); removePhoto(); if (file) file.click(); };
    if (lra) lra.onclick = function () { S.lowResAccepted = true; $("orn-lowres-modal").classList.remove("on"); };

    // text
    var at = $("orn-add-text-btn"); if (at) at.onclick = addText;
    wireTextGroup(1); wireTextGroup(2);

    // mobile top editor
    var tin = $("orn-top-text-editor");
    if (tin) {
      tin.addEventListener("input", function () {
        var t = S.texts[S.sel]; if (t) { t.txt = tin.value; autoFitText(t); syncTextUI(); renderDesign(); }
        updateTopCount();
      });
    }
    var tdone = $("orn-top-editor-done"); if (tdone) tdone.onclick = function () { closeTopEditor(); pushHistory(); };

    // confirm modal
    var cdk = $("orn-confirm-design"), cpb = $("orn-confirm-proceed");
    if (cdk) cdk.onchange = updateConfirmProceed;
    var ccl = $("orn-confirm-cancel"); if (ccl) ccl.onclick = function () {
      // reset the loading view so a later open starts on the form (nightlight parity)
      var lo = $("orn-confirm-loading"), fo = $("orn-confirm-form");
      if (lo) lo.style.display = "none"; if (fo) fo.style.display = "";
      $("orn-confirm-modal").classList.remove("on");
    };
    if (cpb) cpb.onclick = proceedCheckout;
    var rty = $("orn-confirm-retry"); if (rty) rty.onclick = function () {
      var lo = $("orn-confirm-loading"), fo = $("orn-confirm-form");
      if (lo) lo.style.display = "none"; if (fo) fo.style.display = "";
    };

    var cart = $("orn-cart-btn"); if (cart) { cart.onclick = onAddToCart; cart.disabled = true; }

    wireCanvas(); wireMarker();
    window.addEventListener("resize", function () {
      if (S.mode === "design") renderDesign(); else resizeThree();
    });

    syncAll();
    pushHistory();
    renderDesign();
  }

  // ── Edit Design: rehydrate a saved job (?edit=ORN-… from the cart) ───────
  function setEditMode() {
    var lbl = $("orn-cart-label"); if (lbl) lbl.textContent = T("saveDesign", "Save Design");
    var pr = $("orn-cart-price"); if (pr) pr.style.display = "none";     // no price on "Save Design"
    var cp = $("orn-confirm-proceed"); if (cp) cp.textContent = T("saveDesign", "Save Design");
  }
  function rehydrate(jobId, cartLine, from) {
    if (!API) return;
    S.editJobId = jobId; S.editCartLine = cartLine || null; S.editFrom = from || "drawer";
    S.hydrating = true; S.editDirty = false;
    setEditMode();
    var cu = $("orn-canvas-upload"); if (cu) cu.style.display = "none";  // don't flash the upload prompt
    var lo = $("orn-shell-loading"); if (lo) lo.style.display = "flex";
    openModal();
    setStatus(T("loadingDesign", "Loading your design…"));
    fetch(API + "/job/" + encodeURIComponent(jobId) + "/data")
      .then(function (r) { if (!r.ok) throw new Error("job fetch failed (" + r.status + ")"); return r.json(); })
      .then(function (j) {
        var meta = j.meta || {};
        var st = {};
        try { st = JSON.parse(meta.orn_settings || "{}") || {}; } catch (e) {}
        S.jobId = jobId; S.variantId = meta.variant_id || null;
        S.editCleanExt = meta.clean_key || meta.clean_ext || null;   // exact filename (new) or bare ext (legacy) — keeps the original re-editable
        // size first (drives geometry), then placement + text state
        var sz = SIZES.filter(function (s2) { return s2.key === (st.size || meta.orn_size); })[0];
        if (sz && !sz.stub) S.size = sz;
        S.zoom = st.zoom || 1; S.offX = st.offX || 0; S.offY = st.offY || 0;
        S.rotation = st.rotation || 0;
        S.sc = !!st.sc; S.scStrength = 1;         // strength is fixed at 100%
        S.mu = 0.5; S.mv = vEquator();            // sphere-correct is always centred
        S.bgFront = st.bgFront || st.bg || "gray"; S.bgBack = st.bgBack || "gray";
        // front/back text — fall back to legacy single-array `texts` as the front
        var pad2 = function (a) { a = (a && a.length ? a.slice() : [null, null]); while (a.length < 2) a.push(null); return a; };
        S.side = "front";
        S.frontTexts = pad2(st.frontTexts || st.texts);
        S.backTexts = pad2(st.backTexts);
        S.texts = S.frontTexts;
        S.sel = -1;
        var done = function () {
          var lo2 = $("orn-shell-loading"); if (lo2) lo2.style.display = "none";
          var dn = $("orn-design-name"); if (dn) dn.value = meta.design_name || "";   // carry the name into edit
          HIST = []; _imgStack = []; HI = -1;
          syncSideUI(); syncAll(); renderDesign(); pushHistory();   // seeds history while hydrating=true (stays not-dirty)
          setEditMode();
          S.hydrating = false;                          // subsequent edits now mark the design dirty
          updateCartEnabled();                          // "Save Design" starts greyed until a change
          setStatus("");                                // no persistent "editing" badge (nightlight has none)
        };
        // hasImg records whether the saved design actually had a photo — without
        // it, older jobs would wrongly load the baked texture as the "photo"
        if (st.hasImg === false || (!meta.clean_ext && st.hasImg !== true)) { done(); return; }
        var im = new Image();
        im.onload = function () {
          S.origImg = im; applyRotation();
          done();
        };
        im.onerror = done;
        im.src = j.image_base64;
      })
      .catch(function (e) {
        console.error("[orn] rehydrate failed", e);
        S.hydrating = false;
        var lo3 = $("orn-shell-loading"); if (lo3) lo3.style.display = "none";
        var cu2 = $("orn-canvas-upload"); if (cu2) cu2.style.display = "";
        setStatus("Couldn't load the saved design", "err");
      });
  }
  (function () {
    try {
      var q = new URLSearchParams(location.search);
      var ej = q.get("edit");
      if (ej && ej.indexOf("ORN-") === 0) {
        var boot = function () { rehydrate(ej, q.get("cart_line"), q.get("from")); };
        if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", boot);
        else setTimeout(boot, 0);
      }
    } catch (e) {}
  })();

  // texture export hook (debug / tests)
  window.exportOrnamentTexture = function () { bake(EXPORT_W); return texCanvas.toDataURL("image/png"); };

  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", wire);
  else wire();
})();
